import { Injectable } from "@angular/core";
import { HttpClient, HttpParams } from "@angular/common/http";
import { Observable } from "rxjs";
import { FormGroup, AbstractControl } from "@angular/forms";
import { ConfigService } from "src/app/services/config.service";
import { AuthService } from "src/app/pages/auth/authServices";

export const PI_PHONE_CALL_SERVICE_LABEL = "Attorney Consultation - Phone Call";

export const PI_MISSING_FEE_CONTACT_MESSAGE =
  "No default fee is configured for this Physician. Please contact Super Admin to update the physician fee schedule.";

interface RolePermissionComponent {
  component?: string;
  subComponent?: Array<{
    subComponentName?: string;
    permissions?: Array<{ name?: string; isPermission?: boolean }>;
  }>;
}

/** "New Personal Injury" visibility; also honours roles saved before PI moved into Encounter Tabs. */
export function canCreatePiEncounter(
  permissions: RolePermissionComponent[] | null | undefined
): boolean {
  const grants = (sub?: RolePermissionComponent["subComponent"][number]) =>
    !!sub?.permissions?.some(
      (p) => p.name === "Create PI Encounter" && p.isPermission
    );
  const find = (component: string) =>
    (permissions || []).find((c) => c.component === component);
  return (
    !!find("Encounter Tabs")?.subComponent?.some(grants) ||
    grants(
      find("Pages")?.subComponent?.find(
        (s) => s.subComponentName === "View Encounters"
      )
    ) ||
    grants(
      find("Personal Injury")?.subComponent?.find(
        (s) => s.subComponentName === "Create PI Encounter"
      )
    )
  );
}

/** PI encounter form + add-service dropdown — history is only via In-Person Evaluation checkbox. */
export const PI_SERVICE_OPTIONS = [
  {
    code: "ATTORNEY_CONSULT_PHONE",
    label: PI_PHONE_CALL_SERVICE_LABEL,
  },
  { code: "IN_PERSON_EVAL", label: "In-Person Evaluation" },
  { code: "RECORD_REVIEW", label: "Physician Record Review" },
  { code: "REPORT_STANDALONE", label: "Report" },
  { code: "DEPOSITION", label: "Deposition" },
  { code: "COURT_APPEARANCE", label: "Court Appearance" },
] as const;

export type PiServiceOption = (typeof PI_SERVICE_OPTIONS)[number];

/** Add-service dialog uses the same service list as the encounter form. */
export const PI_ADD_SERVICE_OPTIONS = [...PI_SERVICE_OPTIONS] as const;

export type PiAddServiceOption = (typeof PI_ADD_SERVICE_OPTIONS)[number];

/** Legacy codes kept for display when editing encounters created before consolidation. */
export const PI_LEGACY_SERVICE_LABELS: Record<string, string> = {
  ATTORNEY_CONSULT_PHONE: PI_PHONE_CALL_SERVICE_LABEL,
  COURT_APPEARANCE: "Court Appearance",
  DEPOSITION: "Deposition",
  REPORT_STANDALONE: "Report",
  FINALIZATION: "Report",
};

/**
 * User-facing PI service name. REPORT_STANDALONE always displays as "Report".
 */
export function getPiServiceDisplayLabel(
  serviceCode?: string | null,
  serviceLabel?: string | null
): string {
  const code = String(serviceCode || "").trim();
  if (code === "REPORT_STANDALONE" || code === "FINALIZATION") {
    return "Report";
  }
  const fromOptions = PI_SERVICE_OPTIONS.find((o) => o.code === code)?.label;
  if (fromOptions) {
    return fromOptions;
  }
  const legacy = PI_LEGACY_SERVICE_LABELS[code];
  if (legacy) {
    return legacy;
  }
  const label = String(serviceLabel || "").trim();
  if (/standalone/i.test(label)) {
    return "Report";
  }
  return label || code || "Service";
}

/** Short names for Active/Completed + duplicate-active messaging. */
export const PI_SERVICE_SHORT_LABELS: Record<string, string> = {
  ATTORNEY_CONSULT_PHONE: PI_PHONE_CALL_SERVICE_LABEL,
  IN_PERSON_EVAL: "In-Person Evaluation",
  RECORD_REVIEW: "Physician Record Review",
  REPORT_STANDALONE: "Report",
  FINALIZATION: "Report",
  DEPOSITION: "Deposition",
  COURT_APPEARANCE: "Court Appearance",
  HISTORY: "History",
};

const PI_OCCURRENCE_COMPLETED: Record<string, string> = {
  ATTORNEY_CONSULT_PHONE: "completedPhoneCall",
  RECORD_REVIEW: "completedPhysicianRecordReview",
  DEPOSITION: "completedDepositionCourt",
  COURT_APPEARANCE: "completedCourtAppearance",
  IN_PERSON_EVAL: "evaluationCompleted",
  HISTORY: "completedHistory",
  REPORT_STANDALONE: "reportCompleted",
  FINALIZATION: "reportCompleted",
};

export const PI_PARENT_TAB_LABELS: Record<string, string> = {
  ATTORNEY_CONSULT_PHONE: PI_PHONE_CALL_SERVICE_LABEL,
  IN_PERSON_EVAL: "Pre-eval",
  RECORD_REVIEW: "Physician Record Review",
  REPORT_STANDALONE: "Report",
  FINALIZATION: "Report",
  DEPOSITION: "Deposition",
  COURT_APPEARANCE: "Court Appearance",
};

export const PI_PARENT_SERVICE_CODES = new Set([
  "ATTORNEY_CONSULT_PHONE",
  "IN_PERSON_EVAL",
  "RECORD_REVIEW",
  "REPORT_STANDALONE",
  "FINALIZATION",
  "DEPOSITION",
  "COURT_APPEARANCE",
]);

/** Primary tab that drives parent/main service status. */
export const PI_SERVICE_MAIN_TAB_KEY: Record<string, string> = {
  ATTORNEY_CONSULT_PHONE: "phone_call",
  IN_PERSON_EVAL: "evaluation",
  RECORD_REVIEW: "physician_record_review",
  REPORT_STANDALONE: "report",
  FINALIZATION: "report",
  DEPOSITION: "deposition",
  COURT_APPEARANCE: "court_appearance",
};

export function getPiServiceMainTabKey(
  serviceCode?: string | null
): string | null {
  const code =
    serviceCode === "FINALIZATION"
      ? "REPORT_STANDALONE"
      : String(serviceCode || "");
  return PI_SERVICE_MAIN_TAB_KEY[code] || null;
}

export type PiServiceTab = {
  tabKey?: string;
  status?: string | null;
  status_2?: string | null;
  dueDate?: string | Date | null;
  completedDate?: string | Date | null;
  assignedTo?: string | { _id?: string; f_name?: string; name?: string } | null;
  /** Optional reason captured when an In-Person Evaluation is closed as "Not Completed". */
  notCompletedReason?: string | null;
  data?: Record<string, unknown> | null;
};

const PI_TAB_COMPLETED: Record<string, string[]> = {
  phone_call: ["completedPhoneCall"],
  history: ["completedHistory"],
  record: ["recordCompleted", "recordsNotReceivedCompleted"],
  physician_record_review: ["completedPhysicianRecordReview"],
  /**
   * "Not Completed" closes the occurrence exactly like "Completed" — it is a
   * terminal outcome (scheduled Evaluation didn't occur), not a billing signal.
   */
  evaluation: ["evaluationCompleted", "evaluationNotCompleted"],
  editing: ["editorCompleted"],
  report: ["reportCompleted", "reportSubmissionCompleted"],
  deposition: ["completedDepositionCourt"],
  court_appearance: ["completedCourtAppearance", "courtAppearanceNotCompleted"],
};

export const PI_TAB_KEY_META: Record<
  string,
  { label: string; route: string; statusLabel: string; permission: string }
> = {
  phone_call: {
    label: PI_PHONE_CALL_SERVICE_LABEL,
    route: "./phone_call",
    statusLabel: "Phone Call",
    permission: "Phone Call",
  },
  history: {
    label: "History",
    route: "./",
    statusLabel: "History",
    permission: "History",
  },
  record: {
    label: "Record Review",
    route: "./record",
    statusLabel: "Record Review",
    permission: "Record Review",
  },
  physician_record_review: {
    label: "Physician Record Review",
    route: "./physician_record_review",
    statusLabel: "Physician Record Review",
    permission: "Physician Record Review",
  },
  evaluation: {
    label: "Evaluation",
    route: "./evaluation",
    statusLabel: "Evaluation",
    permission: "Evaluation",
  },
  editing: {
    label: "Editing",
    route: "./editor",
    statusLabel: "Editing",
    permission: "Editing",
  },
  report: {
    label: "Report",
    route: "./submit_report",
    statusLabel: "Report",
    permission: "Submit Report",
  },
  deposition: {
    label: "Deposition",
    route: "./deposition",
    statusLabel: "Deposition",
    permission: "Deposition",
  },
  court_appearance: {
    label: "Court Appearance",
    route: "./court_appearance",
    statusLabel: "Court Appearance",
    permission: "Court Appearance",
  },
};

export type PiRecordReviewRequirement =
  | "new_required"
  | "already_reviewed"
  | "in_progress"
  | "external_ror"
  | "not_required";

export const PI_RECORD_REVIEW_REQUIREMENT_LABELS: Record<PiRecordReviewRequirement, string> = {
  new_required: "New Record Review Required",
  already_reviewed: "Records Already Reviewed",
  in_progress: "Record Review In Progress",
  external_ror: "External ROR File Available",
  not_required: "No Record Review Required",
};

/** Records table label: "Image" / "Video" for media files, otherwise the page count ("25p."). */
export function formatRecordFileDisplay(
  doc: { name?: string | null; pageCount?: number | string | null } | null | undefined
): string {
  const ext = String(doc?.name || "").split(".").pop()?.toLowerCase() || "";
  if (["jpg", "jpeg", "png"].includes(ext)) return "Image";
  if (["mp4", "mov", "avi", "webm", "mkv"].includes(ext)) return "Video";
  return `${doc?.pageCount ?? ""}p.`;
}

/** External Records accepted alongside an External ROR: PDF, Word, image, video. */
export const PI_EXTERNAL_RECORD_ACCEPT = ".pdf,.doc,.docx,.jpg,.jpeg,.png,.mp4";

export const PI_EXTERNAL_RECORD_TYPES_LABEL = "PDF, Word document (DOC/DOCX), image (JPG/PNG) or video (MP4)";

export function isPiExternalRecordFile(file: File): boolean {
  return (
    [
      "application/pdf",
      "image/jpeg",
      "image/png",
      "video/mp4",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ].includes(file.type) &&
    /\.(pdf|jpe?g|png|mp4|docx?)$/i.test(file.name)
  );
}

/** Appends newly picked External Records, rejecting unsupported types and duplicates. */
export function mergePiExternalRecordFiles(
  current: File[],
  picked: File[]
): { files: File[]; rejected: string[] } {
  const files = [...current];
  const rejected: string[] = [];
  picked.forEach((file) => {
    if (!isPiExternalRecordFile(file)) {
      rejected.push(file.name);
      return;
    }
    const duplicate = files.some(
      (f) => f.name === file.name && f.size === file.size && f.lastModified === file.lastModified
    );
    if (!duplicate) files.push(file);
  });
  return { files, rejected };
}

export const PI_RECORD_REVIEW_REQUIREMENT_SERVICES = new Set([
  "IN_PERSON_EVAL",
  "RECORD_REVIEW",
  "REPORT_STANDALONE",
  "FINALIZATION",
]);

export interface PiRecordReviewRequirementOption {
  value: PiRecordReviewRequirement;
  label: string;
  disabled?: boolean;
}

const PI_INITIAL_EVAL_REQUIREMENT_OPTIONS: PiRecordReviewRequirementOption[] = [
  { value: "new_required", label: PI_RECORD_REVIEW_REQUIREMENT_LABELS.new_required },
  { value: "external_ror", label: "External Record Review" },
  { value: "not_required", label: PI_RECORD_REVIEW_REQUIREMENT_LABELS.not_required },
];

export const PI_FEE_EDIT_CONTACT_MESSAGE = "To edit the fees, please contact the Super Admin.";

function piPhoneCallFeeValidator(control: AbstractControl): Record<string, boolean> | null {
  const group = control.parent;
  if (group?.get("serviceCode")?.value !== "ATTORNEY_CONSULT_PHONE" || group.get("occurrenceId")?.value) return null;
  if (control.value == null || String(control.value).trim() === "") return { required: true };
  return Number.isFinite(Number(control.value)) && Number(control.value) >= 0 ? null : { invalidFee: true };
}

export function setPiPhoneCallFeeValidators(group: FormGroup): void {
  const control = group.get("feeScheduleAmount");
  control.addValidators(piPhoneCallFeeValidator);
  control.updateValueAndValidity({ emitEvent: false });
}

const PI_OTHER_REQUIREMENT_OPTIONS: PiRecordReviewRequirementOption[] = (
  ["new_required", "external_ror"] as const
).map((value) => ({ value, label: value === "new_required" ? "New Records Required" : "External Records" }));

const PI_ADD_SERVICE_REQUIREMENT_OPTIONS: PiRecordReviewRequirementOption[] = [
  { value: "new_required", label: PI_RECORD_REVIEW_REQUIREMENT_LABELS.new_required },
  { value: "already_reviewed", label: PI_RECORD_REVIEW_REQUIREMENT_LABELS.already_reviewed },
  { value: "in_progress", label: PI_RECORD_REVIEW_REQUIREMENT_LABELS.in_progress },
  { value: "external_ror", label: "External Record Review" },
  { value: "not_required", label: PI_RECORD_REVIEW_REQUIREMENT_LABELS.not_required },
];

/** One cached array per (service, completed RR, active RR) so template bindings stay stable. */
const PI_ADD_SERVICE_REQUIREMENT_CACHE = new Map<string, PiRecordReviewRequirementOption[]>();

/**
 * Case creation offers New/External/No (Evaluation) or New/External (others).
 * Adding a service to an existing case offers New + External (+ No for Evaluation),
 * plus Already Reviewed / In Progress only when this case has a completed / open
 * Record Review to link.
 */
export function getPiRecordReviewRequirementOptions(
  serviceCode: string | null | undefined,
  {
    forExistingCase = false,
    hasCompletedRecordReview = false,
    hasActiveRecordReview = false,
  }: { forExistingCase?: boolean; hasCompletedRecordReview?: boolean; hasActiveRecordReview?: boolean } = {}
): PiRecordReviewRequirementOption[] {
  const code = String(serviceCode || "");
  if (!PI_RECORD_REVIEW_REQUIREMENT_SERVICES.has(code)) return [];
  if (!forExistingCase) {
    return code === "IN_PERSON_EVAL" ? PI_INITIAL_EVAL_REQUIREMENT_OPTIONS : PI_OTHER_REQUIREMENT_OPTIONS;
  }
  const isEval = code === "IN_PERSON_EVAL";
  const key = `${isEval}|${hasCompletedRecordReview}|${hasActiveRecordReview}`;
  if (!PI_ADD_SERVICE_REQUIREMENT_CACHE.has(key)) {
    PI_ADD_SERVICE_REQUIREMENT_CACHE.set(key, PI_ADD_SERVICE_REQUIREMENT_OPTIONS.filter(({ value }) =>
      (value !== "already_reviewed" || hasCompletedRecordReview) &&
      (value !== "in_progress" || hasActiveRecordReview) &&
      (value !== "not_required" || isEval)
    ));
  }
  return PI_ADD_SERVICE_REQUIREMENT_CACHE.get(key);
}

export function piRequirementNeedsLink(requirement: unknown): boolean {
  return requirement === "already_reviewed" || requirement === "in_progress";
}

/** Mirrors backend piServiceNeedsRecordTab: does this occurrence own a Record Review tab? */
export function piServiceNeedsRecordTab(service: {
  serviceCode?: string | null;
  recordReviewRequirement?: string | null;
} | null | undefined): boolean {
  const code = String(service?.serviceCode || "");
  const requirement = service?.recordReviewRequirement || null;
  if (code === "IN_PERSON_EVAL") return requirement === "new_required";
  if (["RECORD_REVIEW", "REPORT_STANDALONE", "FINALIZATION"].includes(code)) {
    return !requirement || requirement === "new_required";
  }
  return false;
}

export interface PiLinkableRecordReview {
  occurrenceId: string;
  label: string;
  dueDate: string | Date | null;
  completed: boolean;
}

/**
 * Record Review tabs on the case that another service can link to.
 * already_reviewed → completed tabs; in_progress → open tabs.
 */
type PiRecordReviewSourceService = {
  occurrenceId?: string | null;
  serviceCode?: string | null;
  recordReviewRequirement?: string | null;
  linkedRecordReviewOccurrenceId?: string | null;
  tabs?: PiServiceTab[] | null;
};

/** Every Record Review tab on the case, numbered in service order. */
function listPiRecordReviews(
  services: PiRecordReviewSourceService[] | null | undefined
): PiLinkableRecordReview[] {
  const rows: PiLinkableRecordReview[] = [];
  for (const service of services || []) {
    const tab = (service?.tabs || []).find((t) => t.tabKey === "record");
    if (!tab || !service?.occurrenceId) continue;
    const due = tab.dueDate ? new Date(String(tab.dueDate)) : null;
    const dueLabel =
      due && !Number.isNaN(due.getTime())
        ? ` (ROR Due ${due.toLocaleDateString("en-US", { timeZone: "UTC" })})`
        : "";
    rows.push({
      occurrenceId: String(service.occurrenceId),
      label: `Record Review ${rows.length + 1} — ${getPiServiceDisplayLabel(service.serviceCode)}${dueLabel}`,
      dueDate: tab.dueDate || null,
      completed: isPiServiceTabComplete(tab),
    });
  }
  return rows;
}

export function listPiLinkableRecordReviews(
  services: PiRecordReviewSourceService[] | null | undefined,
  requirement: unknown
): PiLinkableRecordReview[] {
  if (!piRequirementNeedsLink(requirement)) return [];
  return listPiRecordReviews(services).filter((rr) =>
    requirement === "already_reviewed" ? rr.completed : !rr.completed
  );
}

/** Record Review this occurrence relies on: its linked RR, or its own record tab. */
export function findPiLinkedRecordReview(
  services: PiRecordReviewSourceService[] | null | undefined,
  occurrenceId: string | null | undefined
): PiLinkableRecordReview | null {
  if (!occurrenceId) return null;
  const service = (services || []).find(
    (s) => String(s?.occurrenceId || "") === String(occurrenceId)
  );
  if (!service) return null;
  const targetId = piRequirementNeedsLink(service.recordReviewRequirement)
    ? service.linkedRecordReviewOccurrenceId
    : service.occurrenceId;
  if (!targetId) return null;
  return listPiRecordReviews(services).find((rr) => rr.occurrenceId === String(targetId)) || null;
}

/**
 * Report occurrence owning `tabKey` (editing / report): the URL occurrence itself,
 * else the single Report linked to that Record Review occurrence, else the only Report.
 * Ambiguous matches resolve to null so dates never come from an unrelated occurrence.
 */
export function resolvePiReportOccurrenceId(
  services: PiRecordReviewSourceService[] | null | undefined,
  fromUrl: string | null | undefined,
  tabKey: "editing" | "report"
): string | null {
  const rows = (services || []).filter((s) =>
    (s?.tabs || []).some((t) => t?.tabKey === tabKey)
  );
  const occ = String(fromUrl || "").trim();
  if (occ) {
    if (rows.some((row) => String(row.occurrenceId || "") === occ)) return occ;
    const linked = rows.filter((row) => String(row.linkedRecordReviewOccurrenceId || "") === occ);
    if (linked.length === 1) return String(linked[0].occurrenceId || "") || null;
  }
  return rows.length === 1 ? String(rows[0].occurrenceId || "") || null : null;
}

export function inferPiServiceTabs(service: {
  serviceCode?: string | null;
  historyRequired?: boolean | null;
  recordReviewRequirement?: string | null;
  tabs?: PiServiceTab[] | null;
} | null | undefined): PiServiceTab[] {
  if (Array.isArray(service?.tabs) && service.tabs.length) {
    return service.tabs;
  }
  const code =
    service?.serviceCode === "FINALIZATION"
      ? "REPORT_STANDALONE"
      : String(service?.serviceCode || "");
  const tabs: PiServiceTab[] = [];
  const needsRecord = piServiceNeedsRecordTab(service);
  if (code === "HISTORY") {
    tabs.push({ tabKey: "history" });
  } else if (code === "ATTORNEY_CONSULT_PHONE") {
    tabs.push({ tabKey: "phone_call" });
  } else if (code === "IN_PERSON_EVAL") {
    /**
     * Prefer sibling HISTORY row for History workflow. Only put history on
     * Pre-eval when inferring empty tabs AND historyRequired (legacy).
     */
    if (service?.historyRequired) {
      tabs.push({ tabKey: "history" });
    }
    if (needsRecord) tabs.push({ tabKey: "record" });
    tabs.push({ tabKey: "evaluation" });
  } else if (code === "RECORD_REVIEW") {
    if (needsRecord) tabs.push({ tabKey: "record" });
    tabs.push({ tabKey: "physician_record_review" });
  } else if (code === "REPORT_STANDALONE" || code === "FINALIZATION") {
    if (needsRecord) tabs.push({ tabKey: "record" });
    tabs.push({ tabKey: "editing" }, { tabKey: "report" });
  } else if (code === "DEPOSITION") {
    tabs.push({ tabKey: "deposition" });
  } else if (code === "COURT_APPEARANCE") {
    tabs.push({ tabKey: "court_appearance" });
  }
  return tabs;
}

/** Flat eval-shell tab order (History before Evaluation for Pre-eval). */
export const PI_FLAT_TAB_ORDER: string[] = [
  "phone_call",
  "history",
  "record",
  "physician_record_review",
  "evaluation",
  "editing",
  "report",
  "deposition",
  "court_appearance",
];

export type PiEvalTabServiceInput = {
  occurrenceId?: string | null;
  serviceCode?: string | null;
  historyRequired?: boolean | null;
  parentOccurrenceId?: string | null;
  createdAt?: string | Date | null;
  tabs?: PiServiceTab[] | null;
};

export type PiEvalTabSequenceItem = {
  label: string;
  route: string;
  queryParams: { occurrenceId: string };
  occurrenceId: string;
  statusLabel: string;
  tabKey: string;
  permission: string;
  /** Status lookup uses this row (HISTORY sibling vs parent). */
  statusOccurrenceId: string;
};

/**
 * Pure PI tab sequence: services by createdAt, sub-tasks before parent main tab,
 * frequency-based labels (suffix only when count > 1). No RBAC / mutation of input.
 */
export function buildPiEvalTabSequence(
  services: PiEvalTabServiceInput[] | null | undefined
): PiEvalTabSequenceItem[] {
  const all = Array.isArray(services) ? [...services] : [];
  const sorted = [...all].sort((a, b) => {
    const ta = a.createdAt ? new Date(a.createdAt).getTime() : Number.NaN;
    const tb = b.createdAt ? new Date(b.createdAt).getTime() : Number.NaN;
    const aOk = Number.isFinite(ta);
    const bOk = Number.isFinite(tb);
    if (aOk && bOk && ta !== tb) {
      return ta - tb;
    }
    if (aOk !== bOk) {
      return aOk ? -1 : 1;
    }
    return String(a.occurrenceId || "").localeCompare(
      String(b.occurrenceId || "")
    );
  });

  const withinOrder = (tabKey: string) => {
    const idx = PI_FLAT_TAB_ORDER.indexOf(tabKey);
    return idx >= 0 ? idx : PI_FLAT_TAB_ORDER.length;
  };

  const parents = sorted.filter((s) =>
    PI_PARENT_SERVICE_CODES.has(String(s.serviceCode || ""))
  );

  const flat = parents.reduce<
    Array<{
      occurrenceId: string;
      statusOccurrenceId: string;
      tabKey: string;
    }>
  >((acc, svc) => {
    const oid = String(svc.occurrenceId || "");
    if (!oid) {
      return acc;
    }
    const code =
      svc.serviceCode === "FINALIZATION"
        ? "REPORT_STANDALONE"
        : String(svc.serviceCode || "");
    let tabs = [...inferPiServiceTabs(svc)];
    const historyChild = all.find(
      (child) =>
        child.serviceCode === "HISTORY" &&
        String(child.parentOccurrenceId || "") === oid
    );

    if (code === "IN_PERSON_EVAL") {
      if (historyChild?.occurrenceId) {
        acc.push({
          occurrenceId: String(historyChild.occurrenceId),
          statusOccurrenceId: String(historyChild.occurrenceId),
          tabKey: "history",
        });
        tabs = tabs.filter((t) => t.tabKey !== "history");
      } else if (
        svc.historyRequired &&
        !tabs.some((t) => t.tabKey === "history")
      ) {
        acc.push({
          occurrenceId: oid,
          statusOccurrenceId: oid,
          tabKey: "history",
        });
      }
    }

    [...tabs]
      .sort(
        (a, b) =>
          withinOrder(String(a.tabKey || "")) -
          withinOrder(String(b.tabKey || ""))
      )
      .forEach((tab) => {
        const tabKey = String(tab.tabKey || "");
        if (!tabKey || (tabKey === "history" && historyChild)) {
          return;
        }
        acc.push({
          occurrenceId: oid,
          statusOccurrenceId: oid,
          tabKey,
        });
      });

    return acc;
  }, []);

  const countByTabKey = flat.reduce<Record<string, number>>((acc, row) => {
    acc[row.tabKey] = (acc[row.tabKey] || 0) + 1;
    return acc;
  }, {});
  const indexByTabKey: Record<string, number> = {};

  return flat
    .map((row) => {
      const meta = PI_TAB_KEY_META[row.tabKey];
      if (!meta) {
        return null;
      }
      indexByTabKey[row.tabKey] = (indexByTabKey[row.tabKey] || 0) + 1;
      const suffix =
        countByTabKey[row.tabKey] > 1
          ? ` ${indexByTabKey[row.tabKey]}`
          : "";
      return {
        label: `${meta.label}${suffix}`,
        route: meta.route,
        queryParams: { occurrenceId: row.occurrenceId },
        occurrenceId: row.occurrenceId,
        statusLabel: meta.statusLabel,
        tabKey: row.tabKey,
        permission: meta.permission,
        statusOccurrenceId: row.statusOccurrenceId,
      };
    })
    .filter((row): row is PiEvalTabSequenceItem => row != null);
}



/**
 * Deep-link into eval shell for a PI encounter: first parent service's first tab.
 */
export function resolvePiEvalNavigation(
  encounterId: string,
  services?: Array<{
    occurrenceId?: string | null;
    serviceCode?: string | null;
    historyRequired?: boolean | null;
    tabs?: PiServiceTab[] | null;
  }> | null
): { commands: string[]; queryParams: { id: string; occurrenceId?: string } } {
  const id = String(encounterId || "").trim();
  const parents = (services || []).filter((s) =>
    PI_PARENT_SERVICE_CODES.has(String(s.serviceCode || ""))
  );
  const first = parents[0];
  if (first?.occurrenceId) {
    const tabs = inferPiServiceTabs(first);
    const mainKey = getPiServiceMainTabKey(first.serviceCode);
    const preferred =
      (mainKey && tabs.find((t) => t.tabKey === mainKey)) || tabs[0];
    const tabKey = String(preferred?.tabKey || "");
    const meta = PI_TAB_KEY_META[tabKey];
    const segment = String(meta?.route || "./").replace("./", "");
    return {
      commands: segment ? ["/reports/oqme", segment] : ["/reports/oqme"],
      queryParams: {
        id,
        occurrenceId: String(first.occurrenceId),
      },
    };
  }
  return {
    commands: ["/reports/oqme"],
    queryParams: { id },
  };
}

export function isPiServiceTabComplete(
  tab: PiServiceTab | null | undefined
): boolean {
  if (!tab?.tabKey) return false;
  const values = PI_TAB_COMPLETED[tab.tabKey] || [];
  return (
    values.includes(String(tab.status || "")) ||
    values.includes(String(tab.status_2 || ""))
  );
}

export interface PiOccurrenceTabResolution {
  service: { occurrenceId?: string | null; tabs?: PiServiceTab[] | null } | null;
  tab: PiServiceTab | null;
  isComplete: boolean;
}

/**
 * Resolve a PI tab's own occurrence + tab entry from the encounter's
 * piServices[], and whether THAT occurrence's tab (not a shared/aggregate
 * status) is complete. Use this instead of reading EncounterReport scalars
 * (history_status_2, evaluation_status_2, …) directly for a multi-occurrence
 * PI encounter — those scalars are aggregates across ALL occurrences of that
 * service type, not this one occurrence's own status.
 */
export function resolvePiOccurrenceTab(
  services:
    | Array<{ occurrenceId?: string | null; tabs?: PiServiceTab[] | null }>
    | null
    | undefined,
  occurrenceId: string | null | undefined,
  tabKey: string
): PiOccurrenceTabResolution {
  const occ = String(occurrenceId || "");
  const service =
    (services || []).find((s) => String(s.occurrenceId || "") === occ) ||
    null;
  const tab = service
    ? inferPiServiceTabs(service).find((t) => t.tabKey === tabKey) || null
    : null;
  return {
    service,
    tab,
    isComplete: tab ? isPiServiceTabComplete(tab) : false,
  };
}

/**
 * Calendar date shown as "Date of evaluation" / service date for a PI occurrence.
 * Never Encounter.dateOfAppointment (WC single-instance; often empty on PI).
 * History under IN_PERSON_EVAL uses the parent eval occurrence's serviceDate.
 */
export function resolvePiOccurrenceDisplayDate(
  encounter:
    | {
        piServices?: Array<{
          occurrenceId?: string | null;
          serviceCode?: string | null;
          serviceDate?: string | Date | null;
          historyDate?: string | Date | null;
          parentOccurrenceId?: string | null;
        }> | null;
      }
    | null
    | undefined,
  occurrenceId: string | null | undefined,
  tabKey?: string | null
): string | Date | null {
  const services = encounter?.piServices || [];
  const occ = String(occurrenceId || "").trim();
  if (!occ || !services.length) return null;
  const svc = services.find((s) => String(s.occurrenceId || "") === occ) || null;
  if (!svc) return null;
  if (tabKey === "history" || svc.serviceCode === "HISTORY") {
    if (svc.parentOccurrenceId) {
      const parent = services.find(
        (s) => String(s.occurrenceId || "") === String(svc.parentOccurrenceId)
      );
      return parent?.serviceDate || null;
    }
    return null;
  }
  return svc.serviceDate || null;
}

/**
 * Due date for a PI occurrence tab — tab.dueDate, then historyDate / serviceDate.
 */
export function resolvePiOccurrenceDueDate(
  encounter:
    | {
        piServices?: Array<{
          occurrenceId?: string | null;
          serviceDate?: string | Date | null;
          historyDate?: string | Date | null;
          tabs?: PiServiceTab[] | null;
        }> | null;
      }
    | null
    | undefined,
  occurrenceId: string | null | undefined,
  tabKey: string
): string | Date | null {
  const services = encounter?.piServices || [];
  const occ = String(occurrenceId || "").trim();
  const service =
    services.find((s) => String(s.occurrenceId || "") === occ) || null;
  const tab = service
    ? (service.tabs || []).find((t) => t.tabKey === tabKey) || null
    : null;
  if (tab?.dueDate) return tab.dueDate;
  if (!service) return null;
  if (tabKey === "history") {
    return service.historyDate || service.serviceDate || null;
  }
  return service.serviceDate || null;
}

/**
 * Report service dates entered on the service row, read from THIS occurrence
 * (falling back to its own tab due dates), never from the shared EncounterReport.
 */
export function resolvePiReportServiceDates(
  encounter:
    | {
        piServices?: Array<{
          occurrenceId?: string | null;
          recordReviewDueDate?: string | Date | null;
          editingDueDate?: string | Date | null;
          submitReportDueDate?: string | Date | null;
          tabs?: PiServiceTab[] | null;
        }> | null;
      }
    | null
    | undefined,
  occurrenceId: string | null | undefined
): { recordReviewDueDate: string | Date | null; editingDueDate: string | Date | null; reportDueDate: string | Date | null } {
  const occ = String(occurrenceId || "").trim();
  const service = occ
    ? (encounter?.piServices || []).find((s) => String(s.occurrenceId || "") === occ) || null
    : null;
  const tabDue = (tabKey: string) =>
    (service?.tabs || []).find((t) => t.tabKey === tabKey)?.dueDate || null;
  return {
    recordReviewDueDate: service?.recordReviewDueDate || tabDue("record"),
    editingDueDate: service?.editingDueDate || tabDue("editing"),
    reportDueDate: service?.submitReportDueDate || tabDue("report"),
  };
}

/**
 * Resolve assignee id + status for a PI occurrence tab (History 1 vs History 2, etc.).
 * When occurrenceId is present, tab values win (even if null / Unassigned).
 * Without occurrenceId, keep report-level fallbacks (legacy / WC-shaped clients).
 */
export function resolvePiOccurrenceAssigneeAndStatus(
  services:
    | Array<{ occurrenceId?: string | null; tabs?: PiServiceTab[] | null }>
    | null
    | undefined,
  occurrenceId: string | null | undefined,
  tabKey: string,
  fallbacks: {
    assigneeId?: string | null;
    assigneeObj?: { _id?: string; f_name?: string; name?: string } | null;
    status?: string | null;
    status_2?: string | null;
  } = {}
): {
  assigneeId: string | null;
  assigneeObj: { _id?: string; f_name?: string; name?: string } | null;
  status: string | null;
  status_2: string | null;
} {
  const occ = String(occurrenceId || "").trim();
  if (!occ) {
    return {
      assigneeId: fallbacks.assigneeId
        ? String(fallbacks.assigneeId)
        : null,
      assigneeObj: fallbacks.assigneeObj || null,
      status: fallbacks.status || null,
      status_2: fallbacks.status_2 || null,
    };
  }
  const { tab } = resolvePiOccurrenceTab(services, occ, tabKey);
  if (!tab) {
    return {
      assigneeId: null,
      assigneeObj: null,
      status: fallbacks.status || null,
      status_2: fallbacks.status_2 || null,
    };
  }
  const raw = tab.assignedTo;
  let assigneeId: string | null = null;
  let assigneeObj: { _id?: string; f_name?: string; name?: string } | null =
    null;
  if (raw && typeof raw === "object") {
    assigneeId = raw._id ? String(raw._id) : null;
    assigneeObj = raw;
  } else if (raw) {
    assigneeId = String(raw);
    assigneeObj = { _id: assigneeId };
  }
  return {
    assigneeId,
    assigneeObj,
    status: tab.status || null,
    status_2: tab.status_2 || null,
  };
}

/** PI Physician Home dashboard — fixed service column order (status field = *_status_2). */
export const PI_PHYSICIAN_DASHBOARD_SERVICE_COLUMNS: ReadonlyArray<{
  label: string;
  property: string;
}> = [
  { label: PI_PHONE_CALL_SERVICE_LABEL, property: "phone_call_status_2" },
  { label: "History", property: "history_status_2" },
  { label: "In Person Evaluation", property: "evaluation_status_2" },
  { label: "Record Review", property: "record_status_2" },
  { label: "Physician Record Review", property: "physician_record_review_status_2" },
  { label: "Editing", property: "editor_status_2" },
  { label: "Report", property: "report_submission_status_2" },
  { label: "Deposition", property: "deposition_court_status_2" },
  { label: "Court Appearance", property: "court_appearance_status_2" },
];

/** Resolve plaintiff name for PI Home dashboard / task list rows. */
export function resolvePiPlaintiffDisplayName(
  row:
    | {
        fullName?: string | null;
        applicantName?: string | null;
        applicantMiddleName?: string | null;
        applicantLastName?: string | null;
        applicantID?:
          | string
          | {
              fullName?: string | null;
              applicantName?: string | null;
              applicantMiddleName?: string | null;
              applicantLastName?: string | null;
            }
          | null;
      }
    | null
    | undefined
): string {
  if (!row) {
    return "Unknown";
  }
  const trim = (value: string | null | undefined): string =>
    String(value || "").trim();

  const full = trim(row.fullName);
  if (full) {
    return full;
  }

  const fromParts = [row.applicantName, row.applicantMiddleName, row.applicantLastName]
    .map(trim)
    .filter(Boolean)
    .join(" ");
  if (fromParts) {
    return fromParts;
  }

  const lastFirst = [row.applicantLastName, row.applicantName]
    .map(trim)
    .filter(Boolean)
    .join(", ");
  if (lastFirst) {
    return lastFirst;
  }

  const applicant = row.applicantID;
  if (applicant && typeof applicant === "object") {
    const applicantFull = trim(applicant.fullName);
    if (applicantFull) {
      return applicantFull;
    }
    const applicantBuilt = [
      applicant.applicantName,
      applicant.applicantMiddleName,
      applicant.applicantLastName,
    ]
      .map(trim)
      .filter(Boolean)
      .join(" ");
    if (applicantBuilt) {
      return applicantBuilt;
    }
  }

  const first = trim(row.applicantName);
  if (first) {
    return first;
  }

  return "Unknown";
}

/**
 * UI-only IME short codes. Non-IME values return unchanged.
 * Presentation layer only — never mutate saved/API caseType strings.
 */
export function formatPiCaseTypeShortcode(
  caseType: string | null | undefined
): string {
  const raw = String(caseType || "").trim();
  if (!raw) {
    return raw;
  }

  const exact: Record<string, string> = {
    "Independent Medical Exam": "IME",
    "Independent Medical Exam (IME)": "IME",
    "Independent Medical Examination (IME)": "IME",
    "Independent Medical Exam Re-Evaluation": "IMER",
    "Independent Medical Exam Re-evaluation": "IMER",
    "Independent Medical Examination Re-evaluation (IMER)": "IMER",
    "Independent Medical Exam Supplemental": "IMES",
    "Independent Medical Examination Supplemental (IMES)": "IMES",
    "Independent Medical Exam Deposition": "IMED",
    "Independent Medical Examination Deposition (IMED)": "IMED",
    IME: "IME",
    IMER: "IMER",
    IMES: "IMES",
    IMED: "IMED",
  };
  if (exact[raw]) {
    return exact[raw];
  }

  const lower = raw.toLowerCase();
  const isImeFamily =
    lower.includes("independent medical exam") ||
    lower.includes("independent medical examination");
  if (!isImeFamily) {
    return raw;
  }
  if (lower.includes("deposition")) {
    return "IMED";
  }
  if (lower.includes("supplemental")) {
    return "IMES";
  }
  if (
    lower.includes("re-evaluation") ||
    lower.includes("reevaluation") ||
    lower.includes("re evaluation")
  ) {
    return "IMER";
  }
  return "IME";
}

/**
 * Display label for PI case type (dashboard / tables).
 * IME variants → IME/IMER/IMES/IMED; other types keep friendly names.
 */
export function formatPiCaseTypeForDashboard(
  caseType: string | null | undefined
): string {
  const raw = String(caseType || "").trim();
  if (!raw) {
    return "Personal Injury";
  }
  const shortcode = formatPiCaseTypeShortcode(raw);
  if (shortcode !== raw || ["IME", "IMER", "IMES", "IMED"].includes(raw)) {
    return shortcode;
  }
  const mapped: Record<string, string> = {
    "Personal Injury (PI)": "Personal Injury",
    "Personal Injury Case": "Personal Injury",
    "Longshore Harbor (LSH)": "Longshore & Harbor",
    "Longshore Harbor Case": "Longshore & Harbor",
    "Longshore and Harbor": "Longshore & Harbor",
    "Department of Labour (DOL)": "Department of Labor",
    "Department of Labor Case": "Department of Labor",
    "Department of Labor": "Department of Labor",
    PR4: "PR4",
  };
  return mapped[raw] || raw;
}

/** WC: encounterType; PI: caseType with IME short codes (UI only). */
export function formatEncounterTypeForDisplay(row: {
  encounterType?: string | null;
  caseType?: string | null;
}): string {
  if (row?.encounterType === "Personal Injury") {
    const caseType = String(row.caseType || "").trim();
    return caseType
      ? formatPiCaseTypeShortcode(caseType)
      : "Personal Injury";
  }
  const type = String(row?.encounterType || "").trim();
  return type || "--";
}

export type PiDashboardStatusKind =
  | "due"
  | "planned"
  | "completed"
  | "pastDue"
  | "blocked"
  | null;

/** Classify a PI tab *_status_2 value for dashboard badge icons. */
export function classifyPiDashboardStatus(
  status: string | null | undefined
): PiDashboardStatusKind {
  const s = String(status || "").trim();
  if (!s) {
    return null;
  }
  if (
    /UnableToComplete|SubmitUnable|recordsNotReceived|NotCompleted|evaluationNotCompleted/i.test(
      s
    )
  ) {
    return "blocked";
  }
  if (/^pastDue/i.test(s) || s.includes("pastDue")) {
    return "pastDue";
  }
  if (/Completed$/i.test(s) || s === "reportSubmissionCompleted") {
    return "completed";
  }
  if (/^Due/i.test(s)) {
    return "due";
  }
  if (/planned/i.test(s) || /Pending$/i.test(s)) {
    return "planned";
  }
  return null;
}

/** Due date shown in PI Physician dashboard service-column tooltips. */
export function getPiDashboardStatusDueDate(
  row: Record<string, unknown> | null | undefined,
  property: string
): Date | string | null {
  if (!row) {
    return null;
  }
  const fieldByProperty: Record<string, string> = {
    phone_call_status_2: "dateOfAppointment",
    history_status_2: "historyDueDate",
    evaluation_status_2: "dateOfAppointment",
    record_status_2: "recordReviewDueDate",
    physician_record_review_status_2: "physicianRecordReviewDueDate",
    editor_status_2: "editingDueDate",
    report_submission_status_2: "reportDueDate",
    deposition_court_status_2: "depositionCourtDueDate",
    court_appearance_status_2: "depositionCourtDueDate",
  };
  const field = fieldByProperty[property];
  if (field && row[field]) {
    return row[field] as Date | string;
  }
  return null;
}

/** Assignee / owner line for PI Physician dashboard service-column tooltips. */
export function getPiDashboardStatusOwner(
  row: Record<string, unknown> | null | undefined,
  property: string
): string {
  if (!row) {
    return "--";
  }
  const assigneeKeyByProperty: Record<string, string> = {
    history_status_2: "assignToHistorian",
    record_status_2: "assignToRecord",
    evaluation_status_2: "assignToMedicalAssistant",
    editor_status_2: "assignToEditor",
    report_submission_status_2: "assignToReportFinalizer",
    phone_call_status_2: "physician",
    physician_record_review_status_2: "physician",
    deposition_court_status_2: "physician",
    court_appearance_status_2: "physician",
  };
  const key = assigneeKeyByProperty[property];
  const assignee = key ? row[key] : null;
  if (assignee && typeof assignee === "object") {
    const obj = assignee as { f_name?: string; name?: string };
    return obj.f_name || obj.name || "--";
  }
  if (typeof row.physicianName === "string" && row.physicianName) {
    return row.physicianName;
  }
  return "--";
}

export function areAllPiServiceTabsComplete(service: {
  tabs?: PiServiceTab[] | null;
  serviceCode?: string | null;
  completionStatus?: string | null;
} | null | undefined): boolean {
  if (!service) return false;
  const tabs = Array.isArray(service.tabs) ? service.tabs : [];
  if (tabs.length) {
    return tabs.every((t) => isPiServiceTabComplete(t));
  }
  const expected = PI_OCCURRENCE_COMPLETED[String(service.serviceCode || "")];
  return Boolean(expected && service.completionStatus === expected);
}

export function getPiServiceMainStatus(service: {
  serviceCode?: string | null;
  mainStatus?: string | null;
  mainStatus_2?: string | null;
  completionStatus?: string | null;
  tabs?: PiServiceTab[] | null;
  historyRequired?: boolean | null;
} | null | undefined): string {
  if (!service) return "";
  if (service.mainStatus_2 || service.mainStatus) {
    return String(service.mainStatus_2 || service.mainStatus || "");
  }
  const mainKey = getPiServiceMainTabKey(service.serviceCode);
  if (mainKey) {
    const tabs = inferPiServiceTabs(service);
    const mainTab = tabs.find((t) => t.tabKey === mainKey);
    if (mainTab) {
      return String(mainTab.status_2 || mainTab.status || "");
    }
  }
  return String(service.completionStatus || "");
}

/** Service complete when ALL tabs are done (Pre-eval = History + Evaluation). */
export function isPiServiceMainComplete(service: {
  serviceCode?: string | null;
  mainStatus?: string | null;
  mainStatus_2?: string | null;
  completionStatus?: string | null;
  tabs?: PiServiceTab[] | null;
  historyRequired?: boolean | null;
} | null | undefined): boolean {
  if (!service) return false;
  const expected = PI_OCCURRENCE_COMPLETED[String(service.serviceCode || "")];
  if (expected && service.completionStatus === expected) {
    return true;
  }
  return areAllPiServiceTabsComplete(service);
}

const PI_REPORT_DONE = new Set([
  "reportCompleted",
  "reportSubmissionCompleted",
]);

export function getPiServiceShortLabel(serviceCode?: string | null): string {
  const code = String(serviceCode || "").trim();
  return PI_SERVICE_SHORT_LABELS[code] || getPiServiceDisplayLabel(code);
}

export function isPiServiceOccurrenceComplete(
  service: {
    serviceCode?: string | null;
    completionStatus?: string | null;
  } | null | undefined,
  report?: Record<string, unknown> | null
): boolean {
  if (!service) return false;
  const withTabs = service as {
    tabs?: PiServiceTab[];
    mainStatus?: string | null;
    mainStatus_2?: string | null;
    historyRequired?: boolean | null;
  };
  if (Array.isArray(withTabs.tabs) && withTabs.tabs.length > 0) {
    return areAllPiServiceTabsComplete({
      ...service,
      ...withTabs,
    });
  }
  const code = String(service.serviceCode || "");
  const stamped = String(service.completionStatus || "");
  const expected = PI_OCCURRENCE_COMPLETED[code];
  if (expected && stamped === expected) {
    return true;
  }
  if (
    (code === "REPORT_STANDALONE" || code === "FINALIZATION") &&
    (PI_REPORT_DONE.has(stamped) ||
      PI_REPORT_DONE.has(String(report?.["report_submission_status"] || "")) ||
      PI_REPORT_DONE.has(String(report?.["report_submission_status_2"] || "")))
  ) {
    return true;
  }
  if (code === "IN_PERSON_EVAL") {
    return (
      stamped === "evaluationCompleted" ||
      report?.["evaluation_status"] === "evaluationCompleted" ||
      report?.["evaluation_status_2"] === "evaluationCompleted"
    );
  }
  if (code === "HISTORY") {
    return (
      stamped === "completedHistory" ||
      report?.["history_status"] === "completedHistory" ||
      report?.["history_status_2"] === "completedHistory"
    );
  }
  if (code === "ATTORNEY_CONSULT_PHONE") {
    return (
      stamped === "completedPhoneCall" ||
      report?.["phone_call_status"] === "completedPhoneCall"
    );
  }
  if (code === "RECORD_REVIEW") {
    return (
      stamped === "completedPhysicianRecordReview" ||
      report?.["physician_record_review_status"] ===
        "completedPhysicianRecordReview"
    );
  }
  if (code === "DEPOSITION") {
    return (
      stamped === "completedDepositionCourt" ||
      report?.["deposition_court_status"] === "completedDepositionCourt"
    );
  }
  if (code === "COURT_APPEARANCE") {
    return (
      stamped === "completedCourtAppearance" ||
      report?.["court_appearance_status"] === "completedCourtAppearance"
    );
  }
  return false;
}

export function hasActivePiService(
  services: Array<{
    serviceCode?: string | null;
    completionStatus?: string | null;
  }> | null | undefined,
  serviceCode: string,
  report?: Record<string, unknown> | null,
  excludeIndex = -1
): boolean {
  const key =
    serviceCode === "FINALIZATION" ? "REPORT_STANDALONE" : serviceCode;
  return (services || []).some((s, idx) => {
    if (excludeIndex >= 0 && idx === excludeIndex) return false;
    const code =
      s.serviceCode === "FINALIZATION" ? "REPORT_STANDALONE" : s.serviceCode;
    if (code !== key) return false;
    return !isPiServiceOccurrenceComplete(s, report);
  });
}

export function resolvePiTabPayload(encounter: any, occurrenceId: string, tabKey: string): any {
  if (!encounter || !encounter.piServices) return {};
  const svc = encounter.piServices.find((s: any) => String(s.occurrenceId) === String(occurrenceId));
  if (!svc || !svc.tabs) return {};
  const tab = svc.tabs.find((t: any) => t.tabKey === tabKey);
  if (!tab) return {};
  const data =
    tab.data && typeof tab.data === "object" && !Array.isArray(tab.data)
      ? { ...tab.data }
      : {};
  if (tabKey === "deposition" && svc.depositionOnline != null &&
      !Object.prototype.hasOwnProperty.call(data, "deposition_online")) {
    data.deposition_online = svc.depositionOnline;
    data.deposition_meetLink = svc.depositionOnline ? svc.depositionMeetingLink : null;
    data.deposition_addressLine_1 = svc.depositionOnline ? null : svc.depositionAddressLine1;
    data.deposition_addressLine_2 = svc.depositionOnline ? null : svc.depositionAddressLine2;
    data.deposition_city = svc.depositionOnline ? null : svc.depositionCity;
    data.deposition_state = svc.depositionOnline ? null : svc.depositionState;
    data.deposition_zip = svc.depositionOnline ? null : svc.depositionZip;
  }
  // Prefer structured tab.files / singleFile when present (Phase 1.5).
  if (Array.isArray(tab.files) && tab.files.length) {
    if (tabKey === "history") {
      data.uploadHistoryFiles = tab.files;
    }
    if (tabKey === "record") {
      /** ROR template files only — packet rows live in data.uploadRecordFiles. */
      if (!data.uploadRecordReviewFiles) {
        const packetIds = new Set(
          (Array.isArray(data.uploadRecordFiles) ? data.uploadRecordFiles : [])
            .map((f: { id?: string; fileId?: string }) => String(f?.id || f?.fileId || ""))
            .filter(Boolean)
        );
        data.uploadRecordReviewFiles = tab.files.filter(
          (f: { id?: string; fileId?: string }) =>
            !packetIds.has(String(f?.id || f?.fileId || ""))
        );
      }
    }
    if (tabKey === "evaluation" && !data.uploadPateintForms) {
      data.uploadPateintForms = tab.files;
    }
    if (tabKey === "physician_record_review") {
      if (!data.uploadPhysicianRecordReviewFiles) {
        data.uploadPhysicianRecordReviewFiles = tab.files;
      }
    }
    if (tabKey === "phone_call" && tab.data?.piPhoneCall) {
      data.piPhoneCalls = [
        {
          ...tab.data.piPhoneCall,
          occurrenceId: String(occurrenceId),
        },
      ];
    }
  }
  if (
    tabKey === "evaluation" &&
    data.interpreter &&
    !(data.interpreterId && typeof data.interpreterId === "object" && data.interpreterId.firstName)
  ) {
    /** Denormalized interpreter snapshot from PI evaluation Info card. */
    data.interpreterId = data.interpreter;
  }
  if (tab.singleFile && typeof tab.singleFile === "object") {
    if (tabKey === "editing") {
      if (!data.uploadFinalReportID && tab.singleFile.id) {
        data.uploadFinalReportID = tab.singleFile.id;
      }
      if (!data.uploadFinalReportFileName && tab.singleFile.name) {
        data.uploadFinalReportFileName = tab.singleFile.name;
      }
    }
    if (tabKey === "record") {
      if (!data.recordReviewFileID && tab.singleFile.id) {
        data.recordReviewFileID = tab.singleFile.id;
      }
      if (!data.recordReviewFileName && tab.singleFile.name) {
        data.recordReviewFileName = tab.singleFile.name;
      }
    }
    if (tabKey === "report") {
      /** PI Report reuses Editing file — never WC mergeFileId. */
      if (!data.uploadFinalReportID && tab.singleFile.id) {
        data.uploadFinalReportID = tab.singleFile.id;
      }
      if (!data.uploadFinalReportFileName && tab.singleFile.name) {
        data.uploadFinalReportFileName = tab.singleFile.name;
      }
    }
    if (tabKey === "deposition") {
      if (!data.depositionFileId && tab.singleFile.id) {
        data.depositionFileId = tab.singleFile.id;
      }
      if (!data.depositionFileName && tab.singleFile.name) {
        data.depositionFileName = tab.singleFile.name;
      }
    }
  }
  return data;
}

export function getActivePiServiceBlockMessage(serviceCode: string): string {
  const label = getPiServiceShortLabel(serviceCode);
  return `An active ${label} service already exists. Complete the existing ${label} before adding another one.`;
}

/** Prep / Travel time dropdown for Court Appearance. */
export const PI_PREP_TRAVEL_OPTIONS = [
  "Not Added",
  "1 Hour",
  "2 Hours",
  "3 Hours",
  "4 Hours",
] as const;

export type PiPrepTravelOption = (typeof PI_PREP_TRAVEL_OPTIONS)[number];

/** Deposition reserved appearance hours (1–8). */
export const PI_DEPOSITION_RESERVED_HOURS_OPTIONS = [
  1, 2, 3, 4, 5, 6, 7, 8,
] as const;

export type PiDepositionReservedHours =
  (typeof PI_DEPOSITION_RESERVED_HOURS_OPTIONS)[number];

export function formatPiReservedHoursLabel(
  hours: number | null | undefined
): string {
  const n = Number(hours);
  if (!Number.isInteger(n) || n < 1 || n > 8) {
    return "";
  }
  return n === 1 ? "1 Hour" : `${n} Hours`;
}

/** Initial deposition amount = hourly rate × reserved hours. */
export function calcPiDepositionAmount(
  hourlyRate: number | null | undefined,
  reservedHours: number | null | undefined
): number | null {
  const rate = Number(hourlyRate);
  const hours = Number(reservedHours);
  if (!Number.isFinite(rate) || rate < 0) {
    return null;
  }
  if (!Number.isInteger(hours) || hours < 1 || hours > 8) {
    return null;
  }
  return rate * hours;
}

/** Default Phone Call billing unit when physician schedule omits unitMinutes. */
export const DEFAULT_PI_PHONE_CALL_UNIT_MINUTES = 6;

export const PI_FEE_TYPES = {
  FLAT: "FLAT",
  HOURLY: "HOURLY",
  IN_MINUTES: "IN_MINUTES",
} as const;

export type PiFeeType = (typeof PI_FEE_TYPES)[keyof typeof PI_FEE_TYPES];

export const PI_FEE_TYPE_OPTIONS: Array<{ value: PiFeeType; label: string }> = [
  { value: "FLAT", label: "Flat Fee" },
  { value: "HOURLY", label: "Hourly" },
  { value: "IN_MINUTES", label: "In Minutes" },
];

export type PiCourtAppearanceDayType = "FULL_DAY" | "HALF_DAY";

export const PI_COURT_DAY_TYPE_OPTIONS: Array<{
  value: PiCourtAppearanceDayType;
  label: string;
}> = [
  { value: "FULL_DAY", label: "Full Day" },
  { value: "HALF_DAY", label: "Half Day" },
];

export interface PiFeeScheduleEntry {
  feeType: PiFeeType;
  amount: number;
  unitMinutes: number | null;
}

export interface PiPhoneCallFeeEntry {
  amount: number;
  unitMinutes: number;
  feeType?: PiFeeType;
}

const DEFAULT_FEE_TYPE_BY_CODE: Record<string, PiFeeType> = {
  IN_PERSON_EVAL: "FLAT",
  RECORD_REVIEW: "HOURLY",
  REPORT_STANDALONE: "FLAT",
  DEPOSITION: "HOURLY",
  COURT_APPEARANCE_FULL_DAY: "FLAT",
  COURT_APPEARANCE_HALF_DAY: "FLAT",
  /** Physician rate is hourly (e.g. $500/hr); billed in 6-minute increments. */
  ATTORNEY_CONSULT_PHONE: "HOURLY",
  COURT_APPEARANCE: "FLAT",
};

function normalizeFeeType(raw: unknown): PiFeeType | null {
  const t = String(raw || "")
    .trim()
    .toUpperCase()
    .replace(/\s+/g, "_");
  if (t === "FLAT" || t === "FLAT_FEE") return "FLAT";
  if (t === "HOURLY" || t === "HOUR" || t === "PER_HOUR") return "HOURLY";
  if (
    t === "IN_MINUTES" ||
    t === "MINUTES" ||
    t === "IN_MINUTE" ||
    t === "PER_MINUTE" ||
    t === "PER_MINUTES"
  ) {
    return "IN_MINUTES";
  }
  return null;
}

/** Normalize any legacy fee schedule entry into the unified shape. */
export function normalizePiFeeScheduleEntry(
  value: unknown,
  serviceCode?: string
): PiFeeScheduleEntry | null {
  if (value === null || value === undefined || value === "") {
    return null;
  }
  const defaultType =
    (serviceCode && DEFAULT_FEE_TYPE_BY_CODE[serviceCode]) || "FLAT";

  if (typeof value === "number" || typeof value === "string") {
    const amount = Number(value);
    if (!Number.isFinite(amount) || amount < 0) return null;
    return {
      feeType: defaultType,
      amount,
      unitMinutes: defaultType === "IN_MINUTES" ? DEFAULT_PI_PHONE_CALL_UNIT_MINUTES : null,
    };
  }

  if (typeof value === "object" && !Array.isArray(value)) {
    const raw = value as {
      amount?: unknown;
      fee?: unknown;
      feeType?: unknown;
      unitMinutes?: unknown;
    };
    const amountRaw = raw.amount != null ? raw.amount : raw.fee;
    if (amountRaw === null || amountRaw === undefined || amountRaw === "") {
      return null;
    }
    const amount = Number(amountRaw);
    if (!Number.isFinite(amount) || amount < 0) return null;

    let feeType = normalizeFeeType(raw.feeType);
    if (!feeType) {
      /**
       * Only an EXPLICIT unitMinutes marks this as a legacy per-unit
       * snapshot. A bare { amount } for ATTORNEY_CONSULT_PHONE now defaults
       * to HOURLY (defaultType) — that special-case was needed when
       * IN_MINUTES was Phone Call's default fee type; it no longer is.
       */
      if (raw.unitMinutes != null) {
        feeType = "IN_MINUTES";
      } else {
        feeType = defaultType;
      }
    }

    let unitMinutes: number | null = null;
    if (feeType === "IN_MINUTES") {
      const unitRaw = raw.unitMinutes;
      const unit =
        unitRaw === null || unitRaw === undefined || unitRaw === ""
          ? DEFAULT_PI_PHONE_CALL_UNIT_MINUTES
          : Number(unitRaw);
      if (!Number.isInteger(unit) || unit < 1) return null;
      unitMinutes = unit;
    }

    return { feeType, amount, unitMinutes };
  }

  return null;
}

/**
 * Normalize Phone Call fee schedule entry (legacy number or { amount, unitMinutes }).
 */
export function normalizePiPhoneCallFeeEntry(
  value: unknown
): PiPhoneCallFeeEntry | null {
  const entry = normalizePiFeeScheduleEntry(value, "ATTORNEY_CONSULT_PHONE");
  if (!entry) return null;
  return {
    amount: entry.feeType === "IN_MINUTES" ? entry.amount * 60 / (entry.unitMinutes || DEFAULT_PI_PHONE_CALL_UNIT_MINUTES) : entry.amount,
    unitMinutes: entry.unitMinutes || DEFAULT_PI_PHONE_CALL_UNIT_MINUTES,
    feeType: "HOURLY",
  };
}

export function resolvePiFeeScheduleKey(
  serviceCode: string,
  options?: { courtDayType?: string | null }
): string {
  if (serviceCode === "COURT_APPEARANCE") {
    const day = String(options?.courtDayType || "FULL_DAY")
      .trim()
      .toUpperCase();
    return day === "HALF_DAY"
      ? "COURT_APPEARANCE_HALF_DAY"
      : "COURT_APPEARANCE_FULL_DAY";
  }
  return serviceCode;
}

export function resolvePiFeeScheduleEntry(
  schedule: Record<string, unknown> | null | undefined,
  serviceCode: string,
  options?: { courtDayType?: string | null }
): PiFeeScheduleEntry | null {
  if (!schedule || !serviceCode) return null;
  const key = resolvePiFeeScheduleKey(serviceCode, options);
  let entry = normalizePiFeeScheduleEntry(schedule[key], key);
  /** Legacy single COURT_APPEARANCE → Full Day */
  if (
    !entry &&
    key === "COURT_APPEARANCE_FULL_DAY" &&
    schedule["COURT_APPEARANCE"] != null
  ) {
    entry = normalizePiFeeScheduleEntry(
      schedule["COURT_APPEARANCE"],
      "COURT_APPEARANCE_FULL_DAY"
    );
  }
  return entry;
}

/** Resolve fee amount from physician schedule (rate for phone / hourly). */
export function resolvePiFeeScheduleAmount(
  schedule: Record<string, unknown> | null | undefined,
  serviceCode: string,
  options?: { courtDayType?: string | null }
): number | null {
  return resolvePiFeeScheduleEntry(schedule, serviceCode, options)?.amount ?? null;
}

export function resolvePiPhoneCallFeeFromSchedule(
  schedule: Record<string, unknown> | null | undefined
): PiPhoneCallFeeEntry | null {
  const entry = resolvePiFeeScheduleEntry(schedule, "ATTORNEY_CONSULT_PHONE");
  if (!entry) return null;
  return {
    amount: entry.amount,
    unitMinutes: entry.unitMinutes || DEFAULT_PI_PHONE_CALL_UNIT_MINUTES,
    feeType: entry.feeType,
  };
}

/**
 * Billable units = ceil(totalSeconds / (unitMinutes * 60)).
 * Exact boundary = that unit; +1 second starts the next.
 */
export function calcPiPhoneCallBillableUnits(
  totalSeconds: number,
  unitMinutes: number
): number {
  const seconds = Number(totalSeconds);
  const unitMins = Number(unitMinutes);
  if (!Number.isFinite(seconds) || seconds <= 0) {
    return 0;
  }
  if (!Number.isInteger(unitMins) || unitMins < 1) {
    return 0;
  }
  return Math.ceil(seconds / (unitMins * 60));
}

export function roundToCents(amount: number): number {
  return Math.round((Number(amount) + Number.EPSILON) * 100) / 100;
}

function parsePhoneCallDurationSeconds(duration: {
  durationHours?: number | null;
  durationMinutes?: number | null;
  durationSeconds?: number | null;
}): number | null {
  const h = Number(duration?.durationHours ?? 0);
  const m = Number(duration?.durationMinutes ?? 0);
  const s = Number(duration?.durationSeconds ?? 0);
  if (
    !Number.isFinite(h) ||
    h < 0 ||
    !Number.isInteger(h) ||
    !Number.isFinite(m) ||
    m < 0 ||
    m >= 60 ||
    !Number.isInteger(m) ||
    !Number.isFinite(s) ||
    s < 0 ||
    s >= 60 ||
    !Number.isInteger(s)
  ) {
    return null;
  }
  return h * 3600 + m * 60 + s;
}

/**
 * Derive Phone Call fee from an HOURLY physician rate, billed in 6-minute
 * (or `unitMinutes`) increments, rounded up. amount = hourlyRate * (billableMinutes / 60).
 * True zero duration correctly resolves to $0 (not null). Display/preview
 * only — the backend recomputes and is the source of truth for the saved amount.
 */
export function calcPiPhoneCallAmount(
  hourlyRate: number | null | undefined,
  unitMinutes: number | null | undefined,
  duration: {
    durationHours?: number | null;
    durationMinutes?: number | null;
    durationSeconds?: number | null;
  }
): {
  amount: number;
  billableUnits: number;
  billableMinutes: number;
  totalSeconds: number;
} | null {
  const rate = Number(hourlyRate);
  const unitMins =
    unitMinutes == null || unitMinutes === ("" as unknown)
      ? DEFAULT_PI_PHONE_CALL_UNIT_MINUTES
      : Number(unitMinutes);
  if (!Number.isFinite(rate) || rate < 0) {
    return null;
  }
  if (!Number.isInteger(unitMins) || unitMins < 1) {
    return null;
  }
  const totalSeconds = parsePhoneCallDurationSeconds(duration);
  if (totalSeconds === null) {
    return null;
  }
  const billableUnits = calcPiPhoneCallBillableUnits(totalSeconds, unitMins);
  const billableMinutes = billableUnits * unitMins;
  return {
    amount: roundToCents(rate * (billableMinutes / 60)),
    billableUnits,
    billableMinutes,
    totalSeconds,
  };
}

/**
 * Legacy per-unit calculation (amount = ratePerUnit * billableUnits) —
 * preserved for service occurrences whose explicit snapshot is
 * feeScheduleFeeType === "IN_MINUTES", so their historical pricing doesn't
 * silently change when previewed.
 */
export function calcPiPhoneCallAmountLegacyInMinutes(
  ratePerUnit: number | null | undefined,
  unitMinutes: number | null | undefined,
  duration: {
    durationHours?: number | null;
    durationMinutes?: number | null;
    durationSeconds?: number | null;
  }
): {
  amount: number;
  billableUnits: number;
  billableMinutes: number;
  totalSeconds: number;
} | null {
  const rate = Number(ratePerUnit);
  const unitMins =
    unitMinutes == null || unitMinutes === ("" as unknown)
      ? DEFAULT_PI_PHONE_CALL_UNIT_MINUTES
      : Number(unitMinutes);
  if (!Number.isFinite(rate) || rate < 0) {
    return null;
  }
  if (!Number.isInteger(unitMins) || unitMins < 1) {
    return null;
  }
  const totalSeconds = parsePhoneCallDurationSeconds(duration);
  if (totalSeconds === null) {
    return null;
  }
  const billableUnits = calcPiPhoneCallBillableUnits(totalSeconds, unitMins);
  return {
    amount: roundToCents(rate * billableUnits),
    billableUnits,
    billableMinutes: billableUnits * unitMins,
    totalSeconds,
  };
}

export const PI_CASE_BILLING_TYPE = {
  RETAINER: "RETAINER",
  BILL: "BILL",
} as const;

export type PiCaseBillingType =
  (typeof PI_CASE_BILLING_TYPE)[keyof typeof PI_CASE_BILLING_TYPE];

/**
 * Service-specific date field label (never generic "Service Date" for known PI services).
 * Deposition → Date of Deposition
 * Court Appearance → Date of Court Appearance
 * Report → Report Due Date
 */
export function getPiServiceDateLabel(serviceCode: string | null | undefined): string {
  switch (String(serviceCode || "")) {
    case "DEPOSITION":
      return "Date of Deposition";
    case "COURT_APPEARANCE":
      return "Date of Court Appearance";
    case "REPORT_STANDALONE":
      return "Report Due Date";
    case "IN_PERSON_EVAL":
      return "Appointment Date & Time";
    case "RECORD_REVIEW":
      return "Physician Record Review Due Date";
    case "ATTORNEY_CONSULT_PHONE":
      return "Phone Call Date & Time";
    default:
      return "Date";
  }
}

export const PI_CASE_TYPE_OPTIONS = [
  "Personal Injury (PI)",
  "Longshore Harbor (LSH)",
  "Department of Labour (DOL)",
  "Independent Medical Exam (IME)",
  "PR4",
  "CSC",
  "CSCR",
] as const;

/** Parent "Case type" value that reveals the IME Subtype dropdown. */
export const PI_IME_PARENT_LABEL = "Independent Medical Exam (IME)";

export const PI_IME_SUBTYPE_OPTIONS = [
  "Independent Medical Examination (IME)",
  "Independent Medical Examination Re-evaluation (IMER)",
  "Independent Medical Examination Supplemental (IMES)",
  "Independent Medical Examination Deposition (IMED)",
] as const;

/** Case types that show the Insurance company section on the PI encounter form. */
export const PI_INSURANCE_CASE_TYPES = [
  "PR4",
  "Department of Labour (DOL)",
  "Longshore Harbor (LSH)",
  PI_IME_PARENT_LABEL,
  ...PI_IME_SUBTYPE_OPTIONS,
  /** Legacy labels kept for older encounters */
  "IME",
  "IMER",
  "IMES",
  "IMED",
  "Department of Labor Case",
  "Longshore Harbor Case",
  "Department of Labor",
  "Longshore and Harbor",
] as const;

export function isPiInsuranceCaseType(caseType: string | null | undefined): boolean {
  if (!caseType) {
    return false;
  }
  return (PI_INSURANCE_CASE_TYPES as readonly string[]).includes(caseType);
}

export const PI_REQUESTING_SIDE_OPTIONS = [
  "Plaintiff Attorney",
  "Defense Attorney",
] as const;

/** Legacy label still accepted for older cases — never shown in the UI. */
export const PI_LEGACY_DEFENSE_ATTORNEY_LABEL = "Defendant Attorney";

/** Normalizes the legacy "Defendant Attorney" label to the current "Defense Attorney" for display. */
export function normalizePiRequestingSideLabel(
  reqParty: string | null | undefined
): string {
  return reqParty === PI_LEGACY_DEFENSE_ATTORNEY_LABEL
    ? "Defense Attorney"
    : reqParty || "";
}

/** Build Angular router navigation for a PI task row (Record Review, History, etc.). */
export function buildPiTaskNavigation(row: {
  actionUrl?: string | null;
  encounterId?: string | { _id?: string } | null;
  occurrenceId?: string | null;
} | null | undefined): {
  commands: string[];
  queryParams: Record<string, string>;
} | null {
  if (!row) {
    return null;
  }
  const encounterId =
    typeof row.encounterId === "object" && row.encounterId
      ? row.encounterId._id
      : row.encounterId;
  const occurrenceId = row.occurrenceId || null;

  if (row.actionUrl) {
    const [path, query = ""] = String(row.actionUrl).split("?");
    const queryParams: Record<string, string> = {};
    query.split("&").forEach((part: string) => {
      const [k, v] = part.split("=");
      if (k) {
        queryParams[decodeURIComponent(k)] = decodeURIComponent(v || "");
      }
    });
    if (encounterId && !queryParams["id"]) {
      queryParams["id"] = String(encounterId);
    }
    if (occurrenceId && !queryParams["occurrenceId"]) {
      queryParams["occurrenceId"] = String(occurrenceId);
    }
    return { commands: [path], queryParams };
  }

  if (!encounterId) {
    return null;
  }
  return {
    commands: ["/reports/oqme/record"],
    queryParams: {
      id: String(encounterId),
      ...(occurrenceId ? { occurrenceId: String(occurrenceId) } : {}),
    },
  };
}

export interface PiReportStandaloneDates {
  recordReviewDate: Date;
  recordReviewDueDate: Date;
  editingDueDate: Date;
  submitReportDueDate: Date;
}

export interface PiReportStandaloneDatesDialogData {
  recordReviewDate?: Date | string | null;
  recordReviewDueDate?: Date | string | null;
  editingDueDate?: Date | string | null;
  submitReportDueDate?: Date | string | null;
}

/** Calendar picker day → UTC date-only (same calendar day in all regions). */
export function pickerToUtcDate(value: unknown): Date | null {
  if (!value) {
    return null;
  }
  if (typeof value === "string") {
    const match = value.trim().match(/^(\d{4})-(\d{2})-(\d{2})(?:$|T)/);
    if (match) {
      const date = new Date(`${match[1]}-${match[2]}-${match[3]}T00:00:00.000Z`);
      return !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === match[0].slice(0, 10) ? date : null;
    }
  }
  const d = value instanceof Date ? value : new Date(String(value));
  if (Number.isNaN(d.getTime())) {
    return null;
  }
  return new Date(
    Date.UTC(d.getFullYear(), d.getMonth(), d.getDate())
  );
}

/**
 * Calendar picker day → `YYYY-MM-DD` (UTC date-only string).
 * Prefer this in API payloads so JSON never shifts the day via local midnight ISO.
 */
export function pickerToUtcDateString(value: unknown): string | null {
  const d = pickerToUtcDate(value);
  if (!d) {
    return null;
  }
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, "0");
  const day = String(d.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/**
 * Datetime picker → ISO with wall-clock digits stored as UTC components
 * (same anti-shift pattern as date-only: selected day/time digits do not move).
 */
export function pickerToIsoDateTime(value: unknown): string | null {
  if (value == null || value === "") {
    return null;
  }
  const localDate = value instanceof Date ? value : new Date(String(value));
  if (Number.isNaN(localDate.getTime())) {
    return null;
  }
  return new Date(
    Date.UTC(
      localDate.getFullYear(),
      localDate.getMonth(),
      localDate.getDate(),
      localDate.getHours(),
      localDate.getMinutes(),
      localDate.getSeconds()
    )
  ).toISOString();
}

/** Inverse of pickerToIsoDateTime for owl-date-time form controls. */
export function isoDateTimeToPicker(value: unknown): Date | null {
  if (value == null || value === "") {
    return null;
  }
  const d = value instanceof Date ? value : new Date(String(value));
  if (Number.isNaN(d.getTime())) {
    return null;
  }
  return new Date(
    d.getUTCFullYear(),
    d.getUTCMonth(),
    d.getUTCDate(),
    d.getUTCHours(),
    d.getUTCMinutes(),
    d.getUTCSeconds()
  );
}

/** Inverse of pickerToUtcDateString / pickerToUtcDate for mat-datepicker controls. */
export function utcDateOnlyToPicker(value: unknown): Date | null {
  if (value == null || value === "") {
    return null;
  }
  if (typeof value === "string") {
    const m = value.trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) {
      return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    }
  }
  const d = value instanceof Date ? value : new Date(String(value));
  if (Number.isNaN(d.getTime())) {
    return null;
  }
  return new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
}

const PI_SERVICE_DATE_FIELDS = [
  "serviceDate",
  "historyDate",
  "recordReviewDate",
  "recordReviewDueDate",
  "physicianRecordReviewDueDate",
  "editingDueDate",
  "finalizationDueDate",
  "submitReportDueDate",
] as const;

/** Normalize every calendar date on a piServices row before create/update/add. */
export function normalizePiServiceDatesForApi(
  row: Record<string, unknown>
): Record<string, unknown> {
  const next: Record<string, unknown> = { ...row };
  if (next.serviceCode === "COURT_APPEARANCE" && "scheduledAppearanceDateTime" in next) {
    const scheduled = next.scheduledAppearanceDateTime;
    const date = scheduled instanceof Date ? scheduled : scheduled ? new Date(String(scheduled)) : null;
    const valid = date && !Number.isNaN(date.getTime());
    next.serviceDate = valid ? date : null;
    next.courtAppearanceTime = valid ? date.toTimeString().substring(0, 5) : null;
  }
  const usesDateTime = ["IN_PERSON_EVAL", "ATTORNEY_CONSULT_PHONE"].includes(
    String(next.serviceCode || "")
  );
  for (const key of PI_SERVICE_DATE_FIELDS) {
    if (key in next && next[key] != null && next[key] !== "") {
      if (usesDateTime && key === "serviceDate") {
        next[key] = pickerToIsoDateTime(next[key]);
      } else {
        next[key] = pickerToUtcDateString(next[key]);
      }
    }
  }
  return next;
}

export function hasReportStandaloneDates(
  value: Partial<PiReportStandaloneDates> | null | undefined
): boolean {
  return !!(
    value?.recordReviewDate &&
    value?.recordReviewDueDate &&
    value?.editingDueDate &&
    value?.submitReportDueDate
  );
}

/**
 * Returns an error message when PRR due dates are missing or out of sequence.
 * Record Review Due Date is only required when a new Record Review is required.
 */
export function validatePhysicianRecordReviewDateSequence(
  recordReviewDueDate: unknown,
  physicianRecordReviewDueDate: unknown,
  requireRecordReviewDue = true
): string | null {
  const rrDue = requireRecordReviewDue ? pickerToUtcDate(recordReviewDueDate) : null;
  const prrDue = pickerToUtcDate(physicianRecordReviewDueDate);
  if (!prrDue) {
    return "Physician Record Review requires Physician Record Review Due Date.";
  }
  if (requireRecordReviewDue && !rrDue) {
    return "Record Review Due Date is required when a new Record Review is required.";
  }
  if (rrDue && rrDue.getTime() >= prrDue.getTime()) {
    return "Record Review Due Date must be before the Physician Record Review Due Date.";
  }
  return null;
}

/**
 * Returns an error message when Report dates are missing or out of sequence.
 * Record Review Date is no longer entered; Record Review Due Date is only
 * required when a new Record Review is required.
 */
export function validateReportStandaloneDateSequence(
  recordReviewDate: unknown,
  recordReviewDueDate: unknown,
  editing: unknown,
  submission: unknown,
  requireRecordReviewDue = true
): string | null {
  const reviewDate = pickerToUtcDate(recordReviewDate);
  const reviewDue = requireRecordReviewDue ? pickerToUtcDate(recordReviewDueDate) : null;
  const editingDate = pickerToUtcDate(editing);
  const submissionDate = pickerToUtcDate(submission);
  if (!editingDate || !submissionDate) {
    return "Report requires Editing Due Date and Report Due Date.";
  }
  if (requireRecordReviewDue && !reviewDue) {
    return "Record Review Due Date is required when a new Record Review is required.";
  }
  if (reviewDate && reviewDue && reviewDue.getTime() < reviewDate.getTime()) {
    return "Record Review Due Date must be on or after the Record Review Date.";
  }
  if (reviewDue && editingDate.getTime() < reviewDue.getTime()) {
    return "Editing Due Date must be on or after the Record Review Due Date.";
  }
  if (submissionDate.getTime() < editingDate.getTime()) {
    return "Report Due Date must be on or after the Editing Due Date.";
  }
  return null;
}

@Injectable({ providedIn: "root" })
export class PiService {
  private apiUrl: string;

  constructor(
    private http: HttpClient,
    private configService: ConfigService,
    private authService: AuthService
  ) {
    this.apiUrl = this.configService.get("apiUrl") + "/admin";
  }

  /** Only Super Admin may enter or override a PI Service Fee (mirrors backend). */
  canEditServiceFee(): boolean {
    const role = this.authService.getRoleName?.() || localStorage.getItem("roleName") || "";
    return role.trim().toUpperCase() === "SUPER ADMIN";
  }

  isPersonalInjuryCaseManager(): boolean {
    const role = this.authService.getRoleName?.() || localStorage.getItem("roleName") || "";
    return role.trim().toUpperCase() === "PERSONAL INJURY CASE MANAGER";
  }

  private roleQuery(): string {
    const role =
      this.authService.getRoleName?.() ||
      localStorage.getItem("roleName") ||
      "";
    return role ? `&roleName=${encodeURIComponent(role)}` : "";
  }

  private encounterRequestBody(payload: Record<string, unknown>, uploads: Array<{ serviceIndex: number; files: File[] }>): Record<string, unknown> | FormData {
    if (!uploads.length) return payload;
    const form = new FormData();
    let fileIndex = 0;
    const externalRecordFileGroups = uploads.map(batch => ({ serviceIndex: batch.serviceIndex,
      fileIndexes: batch.files.map(file => {
        form.append("externalRecordFiles", file, file.name);
        return fileIndex++;
      }),
    }));
    form.append("payload", JSON.stringify({ ...payload, externalRecordFileGroups }));
    return form;
  }

  createEncounter(payload: Record<string, unknown>, uploads: Array<{ serviceIndex: number; files: File[] }> = []): Observable<unknown> {
    return this.http.post(
      `${this.apiUrl}/create_pi_encounter?name=Create PI Encounter${this.roleQuery()}`,
      this.encounterRequestBody(payload, uploads)
    );
  }

  updateEncounter(
    id: string,
    payload: Record<string, unknown>,
    uploads: Array<{ serviceIndex: number; files: File[] }> = []
  ): Observable<unknown> {
    return this.http.post(
      `${this.apiUrl}/update_pi_encounter/${id}?name=Update PI Encounter${this.roleQuery()}`,
      this.encounterRequestBody(payload, uploads)
    );
  }

  getEncounter(id: string): Observable<unknown> {
    return this.http.get(`${this.apiUrl}/get_pi_encounter/${id}`);
  }

  /**
   * JSON body, or multipart (`payload` JSON + `externalRecordFiles[]`) when
   * External Records and ROR files are attached.
   */
  addService(
    id: string,
    payload: Record<string, unknown>,
    externalRecordFiles: File[] = []
  ): Observable<unknown> {
    let body: Record<string, unknown> | FormData = payload;
    if (externalRecordFiles.length) {
      const form = new FormData();
      form.append("payload", JSON.stringify(payload));
      externalRecordFiles.forEach((file) =>
        form.append("externalRecordFiles", file, file.name)
      );
      body = form;
    }
    return this.http.post(
      `${this.apiUrl}/pi_add_service/${id}?name=Add PI Service${this.roleQuery()}`,
      body
    );
  }

  removeService(id: string, occurrenceId: string): Observable<unknown> {
    return this.http.post(
      `${this.apiUrl}/pi_remove_service/${id}?name=Remove PI Service${this.roleQuery()}`,
      { occurrenceId }
    );
  }

  getLedger(id: string): Observable<unknown> {
    return this.http.get(
      `${this.apiUrl}/pi_ledger/${id}?name=View PI Ledger${this.roleQuery()}`
    );
  }

  confirmDebit(id: string, occurrenceId: string): Observable<unknown> {
    return this.http.post(
      `${this.apiUrl}/pi_ledger_confirm_debit/${id}?name=PI Ledger Confirm Debit${this.roleQuery()}`,
      { occurrenceId }
    );
  }

  addRetainer(id: string, amount: number, note?: string, requestKey?: string): Observable<unknown> {
    return this.http.post(
      `${this.apiUrl}/pi_ledger_add_retainer/${id}?name=PI Ledger Add Retainer${this.roleQuery()}`,
      { amount, note, requestKey }
    );
  }

  assignHistorian(
    id: string,
    assignToHistorian: string | null,
    occurrenceId?: string | null
  ): Observable<unknown> {
    return this.http.post(
      `${this.apiUrl}/pi_history_assign/${id}?name=Assign PI Historian${this.roleQuery()}`,
      {
        assignToHistorian,
        ...(occurrenceId ? { occurrenceId } : {}),
      }
    );
  }

  assignRecord(
    id: string,
    assignToRecord: string | null,
    occurrenceId?: string | null
  ): Observable<unknown> {
    return this.http.post(
      `${this.apiUrl}/pi_record_assign/${id}?name=Assign PI Record${this.roleQuery()}`,
      {
        assignToRecord,
        ...(occurrenceId ? { occurrenceId } : {}),
      }
    );
  }

  assignEvaluation(
    id: string,
    assignToMedicalAssistant: string | null,
    occurrenceId?: string | null
  ): Observable<unknown> {
    return this.http.post(
      `${this.apiUrl}/pi_evaluation_assign/${id}?name=Assign PI Evaluation${this.roleQuery()}`,
      {
        assignToMedicalAssistant,
        ...(occurrenceId ? { occurrenceId } : {}),
      }
    );
  }

  /**
   * PI History save (occurrence-scoped). Do not call WC /manageHistory for PI.
   */
  manageHistory(
    id: string,
    payload: {
      reqUpdatePhone: unknown;
      assignToHistorian: string | null;
      history_status: string;
      historyDateTime: unknown;
      dictation: string;
      injuryDetails: unknown;
      historyFiles: File[];
      occurrenceId?: string | null;
    }
  ): Observable<unknown> {
    const formData = new FormData();
    formData.append("reqUpdatePhone", String(payload.reqUpdatePhone));
    formData.append("assignToHistorian", payload.assignToHistorian || "");
    formData.append("history_status", payload.history_status || "");
    formData.append(
      "historyDateTime",
      payload.historyDateTime != null ? String(payload.historyDateTime) : ""
    );
    formData.append("dictation", payload.dictation || "");
    formData.append("injuryDetails", JSON.stringify(payload.injuryDetails || []));
    if (payload.occurrenceId) {
      formData.append("occurrenceId", String(payload.occurrenceId));
    }
    (payload.historyFiles || []).forEach((file) => {
      formData.append("historyFiles", file, file.name);
    });
    return this.http.post(
      `${this.apiUrl}/pi_manage_history/${id}?name=Manage PI History${this.roleQuery()}`,
      formData
    );
  }

  markHistoryComplete(
    id: string,
    occurrenceId?: string | null
  ): Observable<unknown> {
    return this.http.post(
      `${this.apiUrl}/pi_history_complete/${id}?name=Complete PI History${this.roleQuery()}`,
      occurrenceId ? { occurrenceId } : {}
    );
  }

  deleteHistoryFile(
    id: string,
    fileId: string,
    occurrenceId?: string | null
  ): Observable<unknown> {
    return this.http.post(
      `${this.apiUrl}/pi_history_delete_file/${id}?name=Delete PI History File${this.roleQuery()}`,
      {
        fileId,
        ...(occurrenceId ? { occurrenceId } : {}),
      }
    );
  }

  renameHistoryFile(
    id: string,
    fileId: string,
    newName: string,
    occurrenceId?: string | null
  ): Observable<unknown> {
    return this.http.post(
      `${this.apiUrl}/pi_history_rename_file/${id}?name=Rename PI History File${this.roleQuery()}`,
      {
        fileId,
        newName,
        ...(occurrenceId ? { occurrenceId } : {}),
      }
    );
  }

  /**
   * PI Evaluation save (occurrence-scoped). Do not call WC /manageEvaluation for PI.
   */
  manageEvaluation(
    id: string,
    payload: {
      assignToMedicalAssistant: unknown;
      applicantAttendance: unknown;
      timely_cancellation: unknown;
      interpreterAttendance: unknown;
      applicantArrived: unknown;
      interpreterArrived: unknown;
      interpreterFullyCertified: unknown;
      evaluation_status: unknown;
      faceToFace: unknown;
      time_spent_reviewing_sub_rosa: unknown;
      applicantAttendance_2: unknown;
      interpreterAttendance_2: unknown;
      cancelDate: unknown;
      cancelBy: unknown;
      comment: unknown;
      reason_unknown: unknown;
      comment_2: unknown;
      time: unknown;
      comment_3: unknown;
      uploadPateintForms: File[];
      uploadPatientId: File[];
      uploadTemplate: File[];
      uploadDictaion: File[];
      reschedule: unknown;
      interpreterDetailsConfirmed?: boolean;
      notCompletedReason?: string;
      occurrenceId?: string | null;
    }
  ): Observable<unknown> {
    const formData = new FormData();
    formData.append(
      "assignToMedicalAssistant",
      payload.assignToMedicalAssistant != null
        ? String(payload.assignToMedicalAssistant)
        : ""
    );
    formData.append(
      "applicantAttendance",
      payload.applicantAttendance != null
        ? String(payload.applicantAttendance)
        : ""
    );
    formData.append(
      "timely_cancellation",
      payload.timely_cancellation != null
        ? String(payload.timely_cancellation)
        : ""
    );
    formData.append(
      "interpreterAttendance",
      payload.interpreterAttendance != null
        ? String(payload.interpreterAttendance)
        : ""
    );
    formData.append(
      "applicantArrived",
      payload.applicantArrived != null ? String(payload.applicantArrived) : ""
    );
    formData.append(
      "interpreterArrived",
      payload.interpreterArrived != null
        ? String(payload.interpreterArrived)
        : ""
    );
    formData.append(
      "interpreterFullyCertified",
      payload.interpreterFullyCertified != null
        ? String(payload.interpreterFullyCertified)
        : ""
    );
    formData.append(
      "evaluation_status",
      payload.evaluation_status != null ? String(payload.evaluation_status) : ""
    );
    formData.append(
      "faceToFace",
      payload.faceToFace != null ? String(payload.faceToFace) : ""
    );
    formData.append(
      "time_spent_reviewing_sub_rosa",
      payload.time_spent_reviewing_sub_rosa != null
        ? String(payload.time_spent_reviewing_sub_rosa)
        : ""
    );
    formData.append(
      "applicantAttendance_2",
      payload.applicantAttendance_2 != null
        ? String(payload.applicantAttendance_2)
        : ""
    );
    formData.append(
      "interpreterAttendance_2",
      payload.interpreterAttendance_2 != null
        ? String(payload.interpreterAttendance_2)
        : ""
    );
    formData.append(
      "cancelDate",
      payload.cancelDate != null ? String(payload.cancelDate) : ""
    );
    formData.append(
      "cancelBy",
      payload.cancelBy != null ? String(payload.cancelBy) : ""
    );
    formData.append(
      "comment",
      payload.comment != null ? String(payload.comment) : ""
    );
    formData.append(
      "reason_unknown",
      payload.reason_unknown != null ? String(payload.reason_unknown) : ""
    );
    formData.append(
      "comment_2",
      payload.comment_2 != null ? String(payload.comment_2) : ""
    );
    formData.append(
      "time",
      payload.time != null ? String(payload.time) : ""
    );
    formData.append(
      "comment_3",
      payload.comment_3 != null ? String(payload.comment_3) : ""
    );
    formData.append(
      "reschedule",
      payload.reschedule != null ? String(payload.reschedule) : ""
    );
    if (payload.interpreterDetailsConfirmed !== undefined) {
      formData.append(
        "interpreterDetailsConfirmed",
        String(!!payload.interpreterDetailsConfirmed)
      );
    }
    if (payload.notCompletedReason !== undefined) {
      formData.append("notCompletedReason", payload.notCompletedReason || "");
    }
    if (payload.occurrenceId) {
      formData.append("occurrenceId", String(payload.occurrenceId));
    }
    (payload.uploadPateintForms || []).forEach((file) => {
      formData.append("uploadPateintForms", file, file.name);
    });
    (payload.uploadPatientId || []).forEach((file) => {
      formData.append("uploadPatientId", file, file.name);
    });
    (payload.uploadTemplate || []).forEach((file) => {
      formData.append("uploadTemplate", file, file.name);
    });
    (payload.uploadDictaion || []).forEach((file) => {
      formData.append("uploadDictaion", file, file.name);
    });
    return this.http.post(
      `${this.apiUrl}/pi_manage_evaluation/${id}?name=Manage PI Evaluation${this.roleQuery()}`,
      formData
    );
  }

  markEvaluationComplete(
    id: string,
    occurrenceId?: string | null,
    checklist?: {
      applicantAttendance?: string | null;
      applicantArrived?: boolean;
      interpreterDetailsConfirmed?: boolean;
    }
  ): Observable<unknown> {
    return this.http.post(
      `${this.apiUrl}/pi_evaluation_complete/${id}?name=Complete PI Evaluation${this.roleQuery()}`,
      {
        ...(occurrenceId ? { occurrenceId } : {}),
        ...(checklist?.applicantAttendance
          ? { applicantAttendance: checklist.applicantAttendance }
          : {}),
        ...(checklist &&
        Object.prototype.hasOwnProperty.call(checklist, "applicantArrived")
          ? { applicantArrived: !!checklist.applicantArrived }
          : {}),
        ...(checklist &&
        Object.prototype.hasOwnProperty.call(
          checklist,
          "interpreterDetailsConfirmed"
        )
          ? {
              interpreterDetailsConfirmed: !!checklist.interpreterDetailsConfirmed,
            }
          : {}),
      }
    );
  }

  /** Saves PI plaintiff / interpreter confirmation onto the Evaluation occurrence. */
  savePiEvaluationChecklist(
    reportId: string,
    payload: {
      occurrenceId?: string | null;
      applicantAttendance?: string | null;
      applicantArrived?: boolean;
      interpreterDetailsConfirmed?: boolean;
    }
  ): Observable<unknown> {
    return this.http.post(
      `${this.apiUrl}/manageConfirmation/${reportId}/applicant?name=PI Evaluation Confirmation${this.roleQuery()}`,
      {
        occurrenceId: payload.occurrenceId || null,
        applicantAttendance: payload.applicantAttendance || null,
        ...(Object.prototype.hasOwnProperty.call(payload, "applicantArrived")
          ? { applicantArrived: !!payload.applicantArrived }
          : {}),
        interpreterDetailsConfirmed: !!payload.interpreterDetailsConfirmed,
      }
    );
  }

  deleteEvaluationFile(
    id: string,
    fileId: string,
    type: string = "evaluation",
    occurrenceId?: string | null
  ): Observable<unknown> {
    return this.http.post(
      `${this.apiUrl}/pi_evaluation_delete_file/${id}?name=Delete PI Evaluation File${this.roleQuery()}`,
      {
        fileId,
        type,
        ...(occurrenceId ? { occurrenceId } : {}),
      }
    );
  }

  renameEvaluationFile(
    id: string,
    fileId: string,
    newName: string,
    type: string = "evaluation",
    occurrenceId?: string | null
  ): Observable<unknown> {
    return this.http.post(
      `${this.apiUrl}/pi_evaluation_rename_file/${id}?name=Rename PI Evaluation File${this.roleQuery()}`,
      {
        fileId,
        newName,
        type,
        ...(occurrenceId ? { occurrenceId } : {}),
      }
    );
  }

  /**
   * Evaluation Info — Applicant URI (occurrence-scoped). Do not use WC /evaluationForApplicant.
   */
  evaluationForApplicant(
    id: string,
    payload: {
      uriSymptoms: unknown;
      uriSymptomsNone: unknown;
      uriSignFileName: unknown;
      generalInformationStatus: unknown;
      regulation40: unknown;
      regulationSignFileName: unknown;
      occurrenceId?: string | null;
    }
  ): Observable<{ success?: boolean; data: unknown }> {
    return this.http.post<{ success?: boolean; data: unknown }>(
      `${this.apiUrl}/pi_evaluation_applicant/${id}?name=PI Evaluation Applicant URI${this.roleQuery()}`,
      {
        uriSymptoms: payload.uriSymptoms,
        uriSymptomsNone: payload.uriSymptomsNone,
        uriSignFileName: payload.uriSignFileName,
        generalInformationStatus: payload.generalInformationStatus,
        regulation40: payload.regulation40,
        regulationSignFileName: payload.regulationSignFileName,
        ...(payload.occurrenceId ? { occurrenceId: payload.occurrenceId } : {}),
      }
    );
  }

  /**
   * Evaluation Info — Interpreter URI (occurrence-scoped).
   */
  evaluationForInterpreter(
    id: string,
    payload: {
      interpreterURISymptoms: unknown;
      interpreteruriSymptomsNone: unknown;
      interpreterURISignFileName: unknown;
      firstName: unknown;
      middleName: unknown;
      lastName: unknown;
      certificateNumber: unknown;
      certificateType: unknown;
      expiryDate: unknown;
      certificate: File | string | null;
      occurrenceId?: string | null;
    }
  ): Observable<{ success?: boolean; data: unknown }> {
    const formData = new FormData();
    formData.append(
      "interpreterURISymptoms",
      payload.interpreterURISymptoms != null
        ? typeof payload.interpreterURISymptoms === "string"
          ? payload.interpreterURISymptoms
          : JSON.stringify(payload.interpreterURISymptoms)
        : ""
    );
    formData.append(
      "interpreteruriSymptomsNone",
      payload.interpreteruriSymptomsNone != null
        ? String(payload.interpreteruriSymptomsNone)
        : ""
    );
    formData.append(
      "interpreterURISignFileName",
      payload.interpreterURISignFileName != null
        ? String(payload.interpreterURISignFileName)
        : ""
    );
    formData.append(
      "firstName",
      payload.firstName != null ? String(payload.firstName) : ""
    );
    formData.append(
      "middleName",
      payload.middleName != null ? String(payload.middleName) : ""
    );
    formData.append(
      "lastName",
      payload.lastName != null ? String(payload.lastName) : ""
    );
    formData.append(
      "certificateNumber",
      payload.certificateNumber != null
        ? String(payload.certificateNumber)
        : ""
    );
    formData.append(
      "certificateType",
      payload.certificateType != null ? String(payload.certificateType) : ""
    );
    formData.append(
      "expiryDate",
      payload.expiryDate != null ? String(payload.expiryDate) : ""
    );
    if (payload.certificate != null) {
      formData.append("certificate", payload.certificate as Blob);
    }
    if (payload.occurrenceId) {
      formData.append("occurrenceId", String(payload.occurrenceId));
    }
    return this.http.post<{ success?: boolean; data: unknown }>(
      `${this.apiUrl}/pi_evaluation_interpreter/${id}?name=PI Evaluation Interpreter${this.roleQuery()}`,
      formData
    );
  }

  /**
   * Evaluation Info — Patient vitals (occurrence-scoped). Do not use WC /vitalsFrom.
   */
  evaluationVitals(
    id: string,
    formValues: Record<string, unknown>,
    occurrenceId?: string | null
  ): Observable<{ success?: boolean; data: unknown }> {
    return this.http.post<{ success?: boolean; data: unknown }>(
      `${this.apiUrl}/pi_evaluation_vitals/${id}?name=PI Evaluation Vitals${this.roleQuery()}`,
      {
        ...formValues,
        ...(occurrenceId ? { occurrenceId } : {}),
      }
    );
  }

  /**
   * PI Record Review save (occurrence-scoped). Do not call WC /manageRecordReview for PI.
   */
  manageRecord(
    id: string,
    payload: {
      assignToRecord: string | null;
      record_status: string;
      allRecordsUploads: unknown;
      recordReviewFiles: File[];
      occurrenceId?: string | null;
    }
  ): Observable<unknown> {
    const formData = new FormData();
    formData.append("assignToRecord", payload.assignToRecord || "");
    formData.append("record_status", payload.record_status || "");
    formData.append(
      "allRecordsUploads",
      payload.allRecordsUploads != null
        ? String(payload.allRecordsUploads)
        : ""
    );
    if (payload.occurrenceId) {
      formData.append("occurrenceId", String(payload.occurrenceId));
    }
    (payload.recordReviewFiles || []).forEach((file) => {
      formData.append("recordReviewFiles", file, file.name);
    });
    return this.http.post(
      `${this.apiUrl}/pi_manage_record/${id}?name=Manage PI Record Review${this.roleQuery()}`,
      formData
    );
  }

  manageRecordPqmes(
    id: string,
    allRecordsUploads: unknown,
    occurrenceId?: string | null
  ): Observable<unknown> {
    return this.http.post(
      `${this.apiUrl}/pi_record_pqmes/${id}?name=PI Record PQMES${this.roleQuery()}`,
      {
        allRecordsUploads,
        ...(occurrenceId ? { occurrenceId } : {}),
      }
    );
  }

  markRecordComplete(
    id: string,
    occurrenceId?: string | null
  ): Observable<unknown> {
    return this.http.post(
      `${this.apiUrl}/pi_record_complete/${id}?name=Complete PI Record Review${this.roleQuery()}`,
      occurrenceId ? { occurrenceId } : {}
    );
  }

  deleteRecordFile(
    id: string,
    fileId: string,
    occurrenceId?: string | null
  ): Observable<unknown> {
    return this.http.post(
      `${this.apiUrl}/pi_record_delete_file/${id}?name=Delete PI Record File${this.roleQuery()}`,
      {
        fileId,
        ...(occurrenceId ? { occurrenceId } : {}),
      }
    );
  }

  renameRecordFile(
    id: string,
    fileId: string,
    newName: string,
    occurrenceId?: string | null
  ): Observable<unknown> {
    return this.http.post(
      `${this.apiUrl}/pi_record_rename_file/${id}?name=Rename PI Record File${this.roleQuery()}`,
      {
        fileId,
        newName,
        ...(occurrenceId ? { occurrenceId } : {}),
      }
    );
  }

  /** Physician Record Review — tab-scoped (do not call report.router PRR APIs). */
  savePhysicianRecordReviewDraft(
    id: string,
    payload: {
      minutes?: number | string;
      note?: string;
      durationHours?: number | string;
      durationMinutes?: number | string;
      durationSeconds?: number | string;
      occurrenceId?: string | null;
    }
  ): Observable<{ success: boolean; data: unknown }> {
    return this.http.post<{ success: boolean; data: unknown }>(
      `${this.apiUrl}/pi_physician_record_review_draft/${id}?name=PI PRR Draft${this.roleQuery()}`,
      payload
    );
  }

  markPhysicianRecordReviewComplete(
    id: string,
    occurrenceId?: string | null
  ): Observable<{ success: boolean; data: unknown }> {
    return this.http.post<{ success: boolean; data: unknown }>(
      `${this.apiUrl}/markCompletePiPhysicianRecordReview/${id}?name=Complete PI PRR${this.roleQuery()}`,
      occurrenceId ? { occurrenceId } : {}
    );
  }

  uploadPhysicianRecordReviewMemo(
    id: string,
    file: File,
    occurrenceId?: string | null
  ): Observable<{ success: boolean; data: unknown }> {
    const formData = new FormData();
    formData.append("file", file);
    if (occurrenceId) {
      formData.append("occurrenceId", String(occurrenceId));
    }
    return this.http.post<{ success: boolean; data: unknown }>(
      `${this.apiUrl}/pi_physician_record_review_upload/${id}?name=Upload PI PRR Memo${this.roleQuery()}`,
      formData
    );
  }

  /** Report (Editing + Report tabs) — tab-scoped. */
  assignReportEditor(
    id: string,
    assignToEditor: string | null,
    occurrenceId?: string | null
  ): Observable<{ status?: string; data: unknown }> {
    return this.http.post<{ status?: string; data: unknown }>(
      `${this.apiUrl}/pi_report_assign_editor/${id}?name=PI Assign Editor${this.roleQuery()}`,
      {
        assignToEditor,
        ...(occurrenceId ? { occurrenceId } : {}),
      }
    );
  }

  assignReportFinalizer(
    id: string,
    assignToReportFinalizer: string | null,
    occurrenceId?: string | null
  ): Observable<{ status?: string; data: unknown }> {
    return this.http.post<{ status?: string; data: unknown }>(
      `${this.apiUrl}/pi_report_assign_finalizer/${id}?name=PI Assign Finalizer${this.roleQuery()}`,
      {
        assignToReportFinalizer,
        ...(occurrenceId ? { occurrenceId } : {}),
      }
    );
  }

  completeReportEditing(
    id: string,
    occurrenceId?: string | null
  ): Observable<{ status?: string; data: unknown }> {
    return this.http.post<{ status?: string; data: unknown }>(
      `${this.apiUrl}/pi_report_complete_editing/${id}?name=Complete PI Editing${this.roleQuery()}`,
      occurrenceId ? { occurrenceId } : {}
    );
  }

  completeReportSubmission(
    id: string,
    occurrenceId?: string | null
  ): Observable<{ status?: string; data: unknown }> {
    return this.http.post<{ status?: string; data: unknown }>(
      `${this.apiUrl}/pi_report_complete_submission/${id}?name=Complete PI Report${this.roleQuery()}`,
      occurrenceId ? { occurrenceId } : {}
    );
  }

  /**
   * Submit Report for one PI occurrence — Editing file only (no PDF merge).
   * Body: { reportDatedOn, occurrenceId? }
   */
  submitReport(
    id: string,
    reportDatedOn: string,
    occurrenceId?: string | null,
    reportFileId?: string | null
  ): Observable<{ success?: boolean; data: unknown }> {
    return this.http.post<{ success?: boolean; data: unknown }>(
      `${this.apiUrl}/pi_report_submit/${id}?name=Submit PI Report${this.roleQuery()}`,
      {
        reportDatedOn,
        ...(occurrenceId ? { occurrenceId } : {}),
        ...(reportFileId ? { reportFileId } : {}),
      }
    );
  }

  deleteReportFile(
    id: string,
    fileId: string,
    tabKey?: string | null,
    occurrenceId?: string | null
  ): Observable<unknown> {
    return this.http.post(
      `${this.apiUrl}/pi_report_delete_file/${id}?name=Delete PI Report File${this.roleQuery()}`,
      {
        fileId,
        ...(tabKey ? { tabKey } : {}),
        ...(occurrenceId ? { occurrenceId } : {}),
      }
    );
  }

  /** Phone Call — tab-scoped. */
  savePhoneCallDraft(
    id: string,
    payload: { calls: Array<Record<string, unknown>> }
  ): Observable<{ success: boolean; data: unknown }> {
    return this.http.post<{ success: boolean; data: unknown }>(
      `${this.apiUrl}/pi_phone_call_draft/${id}?name=PI Phone Draft${this.roleQuery()}`,
      payload
    );
  }

  markPhoneCallComplete(
    id: string,
    occurrenceId: string
  ): Observable<{ success: boolean; data: unknown }> {
    return this.http.post<{ success: boolean; data: unknown }>(
      `${this.apiUrl}/markCompletePiPhoneCall/${id}?name=Complete PI Phone${this.roleQuery()}`,
      { occurrenceId }
    );
  }

  reschedulePhoneCall(
    id: string,
    payload: { occurrenceId: string; rescheduledDate: string | Date }
  ): Observable<{ success: boolean; data: unknown }> {
    return this.http.post<{ success: boolean; data: unknown }>(
      `${this.apiUrl}/reschedule_pi_phone_call/${id}?name=Reschedule PI Phone${this.roleQuery()}`,
      payload
    );
  }

  addDepositionTime(
    id: string,
    payload: {
      durationHours?: number | string;
      durationMinutes?: number | string;
      durationSeconds?: number | string;
      occurrenceId?: string | null;
    }
  ): Observable<{ success: boolean; data: unknown }> {
    return this.http.post<{ success: boolean; data: unknown }>(
      `${this.apiUrl}/pi_deposition_court_time/${id}?name=PI Depo Time${this.roleQuery()}`,
      payload
    );
  }

  markDepositionComplete(
    id: string,
    occurrenceId?: string | null
  ): Observable<{ success: boolean; data: unknown }> {
    return this.http.post<{ success: boolean; data: unknown }>(
      `${this.apiUrl}/markCompletePiDepositionCourt/${id}?name=Complete PI Depo${this.roleQuery()}`,
      occurrenceId ? { occurrenceId } : {}
    );
  }

  uploadDepositionTranscript(
    id: string,
    file: File,
    occurrenceId?: string | null
  ): Observable<{ success: boolean; data: unknown }> {
    const formData = new FormData();
    formData.append("file", file);
    if (occurrenceId) {
      formData.append("occurrenceId", String(occurrenceId));
    }
    return this.http.post<{ success: boolean; data: unknown }>(
      `${this.apiUrl}/pi_deposition_court_upload/${id}?name=Upload PI Depo${this.roleQuery()}`,
      formData
    );
  }

  /** Court Appearance — tab-scoped. */
  markCourtAppearanceComplete(
    id: string,
    occurrenceId?: string | null,
    outcome: "completed" | "not_completed" = "completed",
    comments?: string | null
  ): Observable<{ success: boolean; data: unknown }> {
    return this.http.post<{ success: boolean; data: unknown }>(
      `${this.apiUrl}/markCompletePiCourtAppearance/${id}?name=Complete PI Court${this.roleQuery()}`,
      { ...(occurrenceId ? { occurrenceId } : {}), outcome, ...(comments !== undefined ? { comments } : {}) }
    );
  }

  /** Alias used by Court Appearance tab Save Draft. */
  updateCourtAppearanceTime(
    id: string,
    occurrenceId: string | null | undefined,
    payload: Record<string, unknown>
  ): Observable<unknown> {
    return this.updateCourtAppearanceDetails(
      id,
      String(occurrenceId || ""),
      payload
    );
  }

  updateCourtAppearanceDetails(
    id: string,
    occurrenceId: string,
    payload: Record<string, unknown>
  ): Observable<unknown> {
    return this.http.post(
      `${this.apiUrl}/pi_court_appearance_update/${id}?name=Save PI Court Appearance Time${this.roleQuery()}`,
      { occurrenceId, ...payload }
    );
  }

  listCaseFiles(id: string, tabKey?: string, occurrenceId?: string | null): Observable<unknown> {
    let params = new HttpParams();
    if (tabKey) {
      params = params.set("tabKey", tabKey);
    }
    if (occurrenceId) params = params.set("occurrenceId", occurrenceId);
    return this.http.get(`${this.apiUrl}/pi_case_files/${id}`, { params });
  }

  uploadCaseFile(
    id: string,
    tabKey: string,
    file: File,
    occurrenceId?: string | null
  ): Observable<unknown> {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("tabKey", tabKey);
    if (occurrenceId) formData.append("occurrenceId", occurrenceId);
    return this.http.post(
      `${this.apiUrl}/pi_upload_case_file/${id}?name=Upload PI Case File${this.roleQuery()}`,
      formData
    );
  }

  saveServiceDraft(
    id: string,
    payload: {
      tabKey: string;
      serviceCode?: string;
      assigneeId?: string | null;
      status?: string;
      status_2?: string;
      notes?: string;
      [key: string]: unknown;
    }
  ): Observable<unknown> {
    return this.http.post(
      `${this.apiUrl}/pi_service_draft/${id}?name=Save PI Service Draft${this.roleQuery()}`,
      payload
    );
  }

  completeService(
    id: string,
    payload: {
      tabKey: string;
      serviceCode?: string;
      notes?: string;
      [key: string]: unknown;
    }
  ): Observable<unknown> {
    return this.http.post(
      `${this.apiUrl}/pi_service_complete/${id}?name=Complete PI Service${this.roleQuery()}`,
      payload
    );
  }

  deleteCaseFile(id: string, fileId: string): Observable<unknown> {
    return this.http.post(
      `${this.apiUrl}/pi_delete_case_file/${id}?name=Delete PI Case File${this.roleQuery()}`,
      { fileId }
    );
  }

  getPiTaskCounts(assignedTo?: string): Observable<any> {
    let params: any = {};
    if (assignedTo) {
      params.assignedTo = assignedTo;
    }
    return this.http.get(`${this.apiUrl}/pi_tasks_counts`, { params });
  }

  getPiTaskList(
    category: string,
    page: number = 1,
    limit: number = 5,
    assignedTo?: string,
    view?: "table" | "card",
    excludeTabKeys?: string[],
    tabKey?: string
  ): Observable<any> {
    let params: any = {
      category,
      page: String(page),
      limit: String(limit),
    };
    if (assignedTo) {
      params.assignedTo = assignedTo;
    }
    if (view) {
      params.view = view;
    }
    if (excludeTabKeys?.length) {
      params.excludeTabKeys = excludeTabKeys.join(",");
    }
    if (tabKey) {
      params.tabKey = tabKey;
    }
    return this.http.get(`${this.apiUrl}/pi_tasks_list`, { params });
  }

}
